import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  DeviceThermostat as ThermostatIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  TableView as TableViewIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { isEmbeddedByMeptoolhub, navigateToHome } from '../../utils/embed'
import { useUnifiedState, normalizeAHUFormCoolingFields } from '../../context/UnifiedStateContext'
import { UserManualButton } from '../UserManual'
import './AHUSelectionForm.css'
type ProjectInfo = any;
type AHUData = any;




// Tab Components
import EquipmentStandardsTab from './tabs/EquipmentStandardsTab';
import AirflowSelectionTab from './tabs/AirflowSelectionTab';
import FanSelectionTab from './tabs/FanSelectionTab';
import OutputSheetsTab from './tabs/OutputSheetsTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ahu-tabpanel-${index}`}
      aria-labelledby={`ahu-tab-${index}`}
      {...other}
      style={{ display: value === index ? 'block' : 'none', height: '100%', minHeight: '100%' }}
    >
      <Box sx={{ p: 3, height: '100%', minHeight: '100%', bgcolor: 'background.paper' }}>{children}</Box>
    </div>
  )
}

import { FanData } from '../../types/equipment'
import { AHUFormData } from '../../types/ahu-form'
import { AHUCalculator, AHUInputs, calculateHourlyOutdoorCondition } from '../../utils/ahuCalculations'
import { captureElementAsImage } from '../../utils/imageCapture'
import { PsychrometricCalculator } from '../../utils/psychrometric'
import { generatePsychrometricChartData } from '../../utils/psychroChartData'

// UUID 생성 함수 (crypto.randomUUID 대안)
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// 프로젝트 관리 모달 컴포넌트
interface ProjectManagementModalProps {
  open: boolean
  onClose: () => void
  projects: ProjectInfo[]
  currentProject: ProjectInfo | null
  onSelectProject: (projectId: string) => void
  onCreateProject: () => void
  onViewProject: (project: ProjectInfo) => void
  onEditProject: (project: ProjectInfo) => void
  onDeleteProject: (projectId: string) => void
  searchTerm: string
  onSearchTermChange: (term: string) => void
  menuAnchorEl: HTMLElement | null
  onMenuClick: (event: React.MouseEvent<HTMLElement>, projectId: string) => void
  onMenuClose: () => void
  selectedProjectForMenu: string | null
}

const ProjectManagementModal: React.FC<ProjectManagementModalProps> = ({
  open,
  onClose,
  projects,
  currentProject,
  onSelectProject,
  onCreateProject,
  onViewProject,
  onEditProject,
  onDeleteProject,
  searchTerm,
  onSearchTermChange,
  menuAnchorEl,
  onMenuClick,
  onMenuClose,
  selectedProjectForMenu
}) => {
  const filteredProjects = projects.filter(project =>
    project.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.buildingName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.location?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          프로젝트 관리
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* 상단 액션 바 */}
          <Box sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateProject}
              sx={{ flexShrink: 0 }}
            >
              신규 프로젝트 작성
            </Button>
            <TextField
              size="small"
              placeholder="프로젝트 검색..."
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: 'auto', sm: 300 },
                flex: { xs: 1, sm: 'initial' }
              }}
            />
          </Box>

          {/* 프로젝트 카드 목록 */}
          <Grid container spacing={2}>
            {filteredProjects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border: currentProject?.id === project.id ? 2 : 1,
                    borderColor: currentProject?.id === project.id ? 'primary.main' : 'grey.300',
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}
                  onClick={() => onSelectProject(project.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }} component="div">
                        {project.projectName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          onMenuClick(e, project.id)
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }} component="div">
                      🏢 {project.buildingName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }} component="div">
                      📍 {project.location}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }} component="div">
                      🏗️ {project.buildingType}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }} component="div">
                      📏 지상{project.totalFloors}층 / 지하{project.basementFloors}층
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }} component="div">
                      📐 {Number(project.buildingArea).toLocaleString()}㎡
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="textSecondary" component="div">
                        생성일: {project.createdAt}
                      </Typography>
                      {currentProject?.id === project.id && (
                        <Chip label="현재 선택" color="primary" size="small" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* 프로젝트 메뉴 */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={onMenuClose}
      >
        <MenuItem onClick={() => {
          const project = projects.find(p => p.id === selectedProjectForMenu)
          if (project) onViewProject(project)
        }}>
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText>보기</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          const project = projects.find(p => p.id === selectedProjectForMenu)
          if (project) onEditProject(project)
        }}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>수정</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedProjectForMenu && onDeleteProject(selectedProjectForMenu)}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>삭제</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

// AHU 추가 대화상자 컴포넌트
interface AddAHUDialogProps {
  open: boolean
  onClose: () => void
  onSave: (ahuInfo: AHUEquipmentInfo) => void
  currentProject: ProjectInfo | null
  existingAHUs: string[]
}

const AddAHUDialog: React.FC<AddAHUDialogProps> = ({
  open,
  onClose,
  onSave,
  currentProject,
  existingAHUs
}) => {
  const [isInitialized, setIsInitialized] = React.useState(false)

  const generateNextAHUNo = (): string => {
    if (!existingAHUs || existingAHUs.length === 0) return 'AH-001'

    const numbers = existingAHUs
      .map(ahu => {
        const match = ahu.match(/AH-(\d+)/)
        return match ? parseInt(match[1], 10) : 0
      })
      .filter(num => !isNaN(num) && num > 0)

    const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0
    return `AH-${String(maxNum + 1).padStart(3, '0')}`
  }

  const { control, handleSubmit, reset, formState: { errors } } = useForm<AHUEquipmentInfo>({
    defaultValues: {
      ahuNo: 'AH-001',
      systemName: '',
      quantity: '1',
      location: ''
    }
  })

  const onSubmit = (data: AHUEquipmentInfo) => {
    onSave(data)
    reset()
    setIsInitialized(false)
    onClose()
  }

  const handleClose = () => {
    reset()
    setIsInitialized(false)
    onClose()
  }

  React.useEffect(() => {
    if (open && !isInitialized) {
      const nextAHUNo = generateNextAHUNo()
      reset({
        ahuNo: nextAHUNo,
        systemName: '',
        quantity: '1',
        location: ''
      })
      setIsInitialized(true)
    }
  }, [open, isInitialized, reset])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        새 공조기 추가
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 2 }}>
          {currentProject && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <BusinessIcon sx={{ fontSize: 18 }} />
                현재 프로젝트: {currentProject.projectName}
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
                {currentProject.buildingName} | {currentProject.location}
              </Typography>
            </Box>
          )}

          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
            1. 장비 개요
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="ahuNo"
                control={control}
                rules={{ required: 'AHU 번호를 입력하세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="AHU No."
                    fullWidth
                    error={!!errors.ahuNo}
                    helperText={errors.ahuNo?.message}
                    placeholder="AH-001"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="quantity"
                control={control}
                rules={{ required: '수량을 입력하세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="수량"
                    type="number"
                    fullWidth
                    error={!!errors.quantity}
                    helperText={errors.quantity?.message}
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="systemName"
                control={control}
                rules={{ required: '계통 명칭을 입력하세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="계통 명칭"
                    fullWidth
                    error={!!errors.systemName}
                    helperText={errors.systemName?.message}
                    placeholder="B1F 상가"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="location"
                control={control}
                rules={{ required: '설치 위치를 입력하세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="설치 위치"
                    fullWidth
                    error={!!errors.location}
                    helperText={errors.location?.message}
                    placeholder="B3F 기계실"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined" type="button">
            취소
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}
          >
            공조기 추가
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

// AHU 장비 개요 인터페이스
interface AHUEquipmentInfo {
  ahuNo: string
  systemName: string
  quantity: string
  location: string
}

// 외기 온습도 조건 코드 시스템
interface OutdoorCondition {
  code: string
  name: string
  summer: {
    temperature: number
    humidity: number
    dailyRange: number // 일교차 (°C) - ASHRAE 계산용
  }
  winter: {
    temperature: number
    humidity: number
  }
}

// 기본 외기 온습도 조건 데이터
const OUTDOOR_CONDITIONS: OutdoorCondition[] = [
  {
    code: 'A',
    name: '표준 조건',
    summer: { temperature: 31.2, humidity: 69, dailyRange: 10 },
    winter: { temperature: -11.9, humidity: 69 }
  },
  {
    code: 'B',
    name: '일반 조건',
    summer: { temperature: 33.0, humidity: 59.8, dailyRange: 9 },
    winter: { temperature: -14.9, humidity: 69 }
  },
]


// Default values for fan data
const createDefaultFanData = (): FanData => ({
  enabled: true,  // VBA 기본값: 팬 활성화
  type: 'Draw(I)', // VBA 기본값: Draw(I)
  fanType: 'airfoil',
  motorPosition: 'inside',
  airflow: 12000, // Example value
  staticPressure: 550, // Example value
  efficiency: 80, // VBA TextBoxf19 기본값 (%)
  safetyFactor: 20, // VBA TextBoxf20 기본값 (%)
  pressure: 1000, // VBA TextBoxf24 기본값 (Pa)
  motorEfficiency: 85, // 모터 효율 기본값 (%)
  rpm: 750,
  motorPower: 0, // This would be calculated
  calculatedPower: 3.1, // Example value
  ductPressureDrop: {
    supplyDuctFriction: 1.1,
    supplyDuctLength: 40,
    returnDuctFriction: 0.9,
    returnDuctLength: 40,
    supplyAirDevicePressureLoss: 30,
    returnAirDevicePressureLoss: 30,
    preFilterPressureLoss: 50,
    mediumFilterPressureLoss: 125,
    heatingCoilPressureLoss: 25,
    coolingCoilPressureLoss: 80,
    humidifierPressureLoss: 50,
    soundAttenuatorPressureLoss: 40,
    damperPressureLoss: 20,
    otherPressureLoss: 0,
  },
});



const AHUSelectionForm: React.FC = () => {
  const { state, dispatch } = useUnifiedState()
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const savedData = localStorage.getItem('KDCSCOKR_AHUSELECTION_UI');
      const parsedData = savedData ? JSON.parse(savedData) : {};
      return parsedData.activeTab ? parseInt(parsedData.activeTab, 10) : 0;
    } catch {
      return 0;
    }
  })

  // 중복 경고 Snackbar 상태
  const [duplicateWarning, setDuplicateWarning] = useState({
    open: false,
    message: ''
  })

  // AHU 목록은 글로벌 상태에서 가져오기 (현재 프로젝트의 AHU들)
  // useMemo 제거하고 직접 필터링하여 항상 최신 상태 반영
  const currentProjectAHUs: any[] = state.ahus
    .filter((ahu: any) => ahu.projectId === state.currentProjectId);

  // 현재 AHU의 Redux state 확인
  const currentAHUFromRedux = currentProjectAHUs.find((ahu: any) => ahu.id === state.currentAHUId);
  if (currentAHUFromRedux) {
    //console.log('📦 [Redux State] sfLoad%:', currentAHUFromRedux.calculationResults?.sfLoadPercentage, '| rfLoad%:', currentAHUFromRedux.calculationResults?.rfLoadPercentage);
  }

  const currentAHUIndex = currentProjectAHUs.findIndex((ahu: any) => ahu.id === state.currentAHUId)

  // 프로젝트 정보 관련 상태
  const [isProjectInfoExpanded, setIsProjectInfoExpanded] = useState(false)

  // 프로젝트 관리 상태
  const [isProjectManagementOpen, setIsProjectManagementOpen] = useState(false)
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<ProjectInfo | null>(null)
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false)
  const [projectSearchTerm, setProjectSearchTerm] = useState('')
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedProjectForMenu, setSelectedProjectForMenu] = useState<string | null>(null)


  // AHU 추가 대화상자 상태
  const [isAddAHUDialogOpen, setIsAddAHUDialogOpen] = useState(false)

  // 파일 메뉴 상태
  const [fileMenuAnchor, setFileMenuAnchor] = useState<null | HTMLElement>(null)
  const isFileMenuOpen = Boolean(fileMenuAnchor)

  // 초기화 확인 다이얼로그 상태
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)

  // 파일 입력 참조
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 현재 프로젝트 정보 (글로벌 상태에서 가져오기)
  const currentProject: any = state.currentProjectId ? state.projects.find((p: any) => p.id === state.currentProjectId) : null
  const currentAHUData: any = state.currentAHUId ? state.ahus.find((ahu: any) => ahu.id === state.currentAHUId) : null

  // 기존 데이터에 ID가 없으면 추가하는 마이그레이션 로직
  useEffect(() => {
    if (state.isLoaded) {
      // 프로젝트 ID 마이그레이션
      if (state.projects.length > 0) {
        const projectsMissingId = state.projects.filter((project: any) => !project.id)
        if (projectsMissingId.length > 0) {
          projectsMissingId.forEach((project: any) => {
            const updatedProject = {
              ...project,
              id: generateUUID()
            }
            dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject })
          })
        }
      }

      // AHU ID 마이그레이션
      if (state.ahus.length > 0) {
        const ahusMissingId = state.ahus.filter((ahu: any) => !ahu.id)
        if (ahusMissingId.length > 0) {
          ahusMissingId.forEach((ahu: any) => {
            const updatedAHU = {
              ...ahu,
              id: generateUUID(),
              updatedAt: new Date().toISOString()
            }
            dispatch({ type: 'UPDATE_AHU', payload: updatedAHU })
          })
        }
      }

      // 프로젝트에 AHU가 있지만 현재 선택된 AHU가 없는 경우 첫 번째 AHU 자동 선택
      if (state.currentProjectId && !state.currentAHUId) {
        const projectAHUs = state.ahus.filter((ahu: any) => ahu.projectId === state.currentProjectId && ahu.id)
        if (projectAHUs.length > 0) {
          dispatch({ type: 'SET_CURRENT_AHU', payload: projectAHUs[0].id })
        }
      }
    }
  }, [state.isLoaded, state.projects.length, state.ahus.length, state.currentProjectId, state.currentAHUId, dispatch])

  const { control, handleSubmit, watch, setValue, reset, formState } = useForm<any>({
    mode: 'onChange', // 실시간 폼 업데이트 활성화
    defaultValues: {
      ahuNo: 'AH-001',
      systemName: 'B1F 상가',
      quantity: '1',
      location: 'B3F 기계실',
      
      // (1) 공간 정보 기본값
      conditionedArea: '0',       // 공조면적 (m²)
      ceilingHeight: '2.7',       // 층고 (m) 
      occupantCount: '0',         // 인원수 (명)

      // (2) 공조 부하 기본값
      sensibleCoolingLoad: '0',   // 냉방 현열 부하(W)
      latentCoolingLoad: '0',     // 냉방 잠열 부하(W)
      ventilationLightingLoad: '0', // 환기 조명부하(W)
      heatingLoad: '0',           // 난방 부하(W)
      raLightingLoadMode: 'separate' as const, // RA Lighting Load 모드 (기본값: RA 온도상승)
      
      outdoorDesignCondition: 'A',
      outdoorTimeApplication: 'standard' as const, // 기본값: 표준 적용
      outdoorApplicationTime: '15', // 냉방 외기 적용 시각 (사용자 요청 기본값)
      systemType: 'CAV',
      fanType: 'None',
      coolingCoilType: 'Water',
      reheatingCoilType: 'Water',
      preheatingCoilType: 'None',
      heatingCoilType: 'Water',
      commonCoil: false,
      coolingInletTemp: '7',
      coolingOutletTemp: '12',
      coolingSafetyFactor: '10',
      reheatingInletTemp: '60',
      reheatingOutletTemp: '50',
      reheatingSafetyFactor: '10',
      rhReheatTemp: '0',
      preheatingInletTemp: '80',
      preheatingOutletTemp: '70',
      preheatingSafetyFactor: '',
      heatingInletTemp: '80',
      heatingOutletTemp: '70',
      heatingSafetyFactor: '10',
      reheatingHeatCapacity: '',
      preheatingHeatCapacity: '',
      heatingHeatCapacity: '',
      supplyAirTemp: '18',
      humidifierType: 'None',
      humidifierInstallLocation: false,
      waterSupplyTemp: '15',
      humidifierSafetyFactor: '10',
      supplyAirflow: '',
      ventilationAirflow: '',
      exhaustAirflow: '',
      exhaustPurpose: '',
      pressurizationAirflow: '',
      supplyTempDelta: '10',
      saFanTempDelta: '0.7',
      airflowPerPerson: '25',
      directOutdoorAirflow: '',
      
      // 풍량 선정 기준
      supplyAirSelectionCriteria: 'standard',
      outdoorAirSelectionCriteria: 'direct',

      // 배열 회수기 설정
      heatRecoveryType: 'None',             // 배열 회수기 타입 (None, S.H, T.H)
      coolingRecoveryEfficiency: '70',      // 냉방용 회수 효율 (%)
      heatingRecoveryEfficiency: '70',      // 난방용 회수 효율 (%)

      // SF/RF 부하 적용 비율 (0%: 부하 적용 안함, 100%: 전체 부하 적용)
      sfLoadPercentage: '100',              // SF부하 적용 비율 (%) - 기본값 100%
      rfLoadPercentage: '100',              // RF부하 적용 비율 (%) - 기본값 100%

      // 실내 온습도 기본값
      coolingRoomTemp: '26',                // 냉방시 실내온도 (℃)
      coolingRoomHumidity: '55',            // 냉방시 실내 상대습도 (%)
      heatingRoomTemp: '20',                // 난방시 실내온도 (℃)
      heatingRoomHumidity: '40',            // 난방시 실내습도 (%)

      supplyFan: createDefaultFanData(),
      returnFan: createDefaultFanData(),
    }
  })


  // 폼 값들을 실시간으로 감시
  const coolingCoilType = watch('coolingCoilType')
  const preheatingCoilType = watch('preheatingCoilType')
  const heatingCoilType = watch('heatingCoilType')
  const reheatingCoilType = watch('reheatingCoilType')
  const commonCoil = watch('commonCoil')
  const humidifierType = watch('humidifierType')
  const selectedOutdoorCondition = watch('outdoorDesignCondition')
  const supplyAirSelectionCriteria = watch('supplyAirSelectionCriteria')
  const outdoorAirSelectionCriteria = watch('outdoorAirSelectionCriteria')

  // 코일 선택 유효성 검증 (적어도 하나는 선택되어야 함)
  const [coilValidationError, setCoilValidationError] = useState('')
  
  // AH-Sel 시트 관련 상태
  const [showSheetPreview, setShowSheetPreview] = useState(false)
  
  // 팬 선정 데이터 변경 감지를 위한 상태
  const [fanDataTimestamp, setFanDataTimestamp] = useState(Date.now())
  
  // 풍량 계산 강제 업데이트를 위한 상태
  const [airflowCalculationTimestamp, setAirflowCalculationTimestamp] = useState(Date.now())

  // 풍량 선정에서 계산된 습공기 선도 데이터 (고해상도 이미지 포함)
  const [psychrometricData, setPsychrometricData] = useState<{
    points: any[],
    lines: any[],
    image?: string,  // 고해상도 PNG 이미지 (Base64)
    metadata?: any   // 추가 메타데이터
  } | null>(null)

  // 습공기 선도 데이터 업데이트 콜백 (이미지 포함)
  const handlePsychroDataCalculated = (data: {
    points: any[],
    lines: any[],
    image?: string,
    metadata?: any,
    ahuId?: string  // 캡처 시점의 AHU ID
  }) => {
    setPsychrometricData(data)

    // 명시적으로 전달된 ahuId 사용 (타이밍 이슈 방지)
    const targetAHUId = data.ahuId || state.currentAHUId;

    if (targetAHUId) {
      const ahuToUpdate = state.ahus.find(ahu => ahu.id === targetAHUId);
      if (ahuToUpdate) {
        // console.log(`🔍 [handlePsychroDataCalculated] 호출됨:`, {
        //   targetAHUId,
        //   currentAHUId: state.currentAHUId,
        //   ahuNo: ahuToUpdate.ahuNo,
        //   isSameAHU: targetAHUId === state.currentAHUId,
        //   hasImage: !!data.image,
        //   pointsCount: data.points?.length || 0,
        //   imagePreview: data.image ? data.image.substring(0, 50) + '...' : 'none'
        // });

        const dryBulbTemps = data.points
          .map((p: any) => p.dryBulbTemp)
          .filter((temp: number) => !isNaN(temp) && isFinite(temp) && temp > -50 && temp < 80);
        const minTemp = dryBulbTemps.length > 0 ? Math.floor(Math.min(...dryBulbTemps) - 5) : -15;
        const maxTemp = dryBulbTemps.length > 0 ? Math.ceil(Math.max(...dryBulbTemps) + 5) : 40;

        const psychroChartData = { ...data, minTemp, maxTemp };

        // 이미지가 data에 포함되어 있으면 직접 사용 (새로운 방식)
        if (data.image) {
          //console.log(`✅ [${ahuToUpdate.ahuNo}] 습공기선도 이미지 저장 완료 (${data.image.length} bytes)`);

          const updatedAHU = {
            ...ahuToUpdate,
            psychroChartData,
            psychroChartImage: data.image  // 고해상도 PNG 이미지 사용 (덮어쓰기)
          };

          dispatch({ type: 'UPDATE_AHU', payload: updatedAHU });
        } else {
          console.warn(`⚠️ [${ahuToUpdate.ahuNo}] 습공기선도 이미지가 data에 없습니다`);

          const updatedAHU = {
            ...ahuToUpdate,
            psychroChartData
          };

          dispatch({ type: 'UPDATE_AHU', payload: updatedAHU });
        }
      } else {
        console.error(`❌ targetAHUId ${targetAHUId}에 해당하는 AHU를 찾을 수 없음`);
      }
    } else {
      console.warn(`⚠️ targetAHUId가 없음, 습공기선도 데이터 저장 불가`);
    }
  }
  
  // 디버깅용: localStorage의 모든 키 확인
  const debugLocalStorage = () => {
    // Debug logs removed
  }
  
  // 팬 선정 데이터 변경 감지
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith('fan-selection-form-data')) {
        setFanDataTimestamp(Date.now())
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // localStorage 고아 데이터 정리
  useEffect(() => {
    if (state.isLoaded && currentProjectAHUs.length > 0) {
      const allKeys = Object.keys(localStorage).filter(key => key.startsWith('KDCSCOKR_AHUSELECTION_FORM_'));
      const validAHUIds = new Set(currentProjectAHUs.map(ahu => ahu.id));
      let cleanupCount = 0;

      allKeys.forEach(key => {
        const ahuId = key.replace('KDCSCOKR_AHUSELECTION_FORM_', '');
        if (!validAHUIds.has(ahuId)) {
          localStorage.removeItem(key);
          cleanupCount++;
        }
      });

      // Cleanup completed silently
    }
  }, [state.isLoaded, currentProjectAHUs])
  
  // 컴포넌트 마운트 시 풍량 계산 강제 실행
  useEffect(() => {
    setAirflowCalculationTimestamp(Date.now())
  }, [currentProject?.currentAHUId])



  // 풍량 계산 로직에 필요한 개별 필드들을 watch
  const sensibleCoolingLoad = watch('sensibleCoolingLoad');
  const latentCoolingLoad = watch('latentCoolingLoad');
  const heatingLoad = watch('heatingLoad');
  const ventilationLightingLoad = watch('ventilationLightingLoad');
  const supplyTempDelta = watch('supplyTempDelta');
  const occupantCount = watch('occupantCount');
  const airflowPerPerson = watch('airflowPerPerson');
  const directOutdoorAirflow = watch('directOutdoorAirflow');
  const exhaustAirflow = watch('exhaustAirflow');
  const pressurizationAirflow = watch('pressurizationAirflow');
  const directSupplyAirflow = watch('directSupplyAirflow');

  // 풍량 계산 로직 (선정 기준에 따라)
  const calculatedAirflows = useMemo(() => {
    // dependency 배열의 값들을 직접 사용 (watch() 재호출 방지)
    let calculatedSupplyAirflow = 0;

    // 급기량 계산 (VBA 원본 기준에 따라)
    switch (supplyAirSelectionCriteria) {
      case 'standard': // A: 표준 (급기 상태점 기준) - VBA OB6
        {
          const sensibleLoad = parseFloat(sensibleCoolingLoad) || 0; // W (냉방 현열 부하)
          const latentLoad = parseFloat(latentCoolingLoad) || 0; // W (냉방 잠열 부하)
          const heatingLoadValue = parseFloat(heatingLoad) || 0; // W (난방 부하)
          const ventilationLighting = parseFloat(ventilationLightingLoad) || 0; // W (환기 조명 부하)
          const supplyTempDeltaValue = parseFloat(supplyTempDelta); // 급기 온도차

          // 급기(△T)가 없거나 0이면 급기량 계산 불가 (VBA의 Exit Sub 로직)
          if (!supplyTempDeltaValue || supplyTempDeltaValue === 0) {
            calculatedSupplyAirflow = 0;
            break;
          }

          // 냉방/난방 모드 감지: 냉방 현열 또는 잠열 부하가 있으면 냉방 모드
          const isCoolingMode = sensibleLoad > 0 || latentLoad > 0;

          let rawSupplyAirflow = 0;
          if (isCoolingMode) {
            // 냉방 모드: VBA 공식 - 급기량 = (냉방 현열 부하 + 환기 조명 부하) / (0.335 * 급기온도차)
            // VBA ahucode0.bas:2126-2127
            // ahdata.Cells(7 + R + k - 1, 45) = Application.RoundUp((ahdata.Cells(7 + R + k - 1, 11) + ahdata.Cells(7 + R + k - 1, 14)) / (0.335 * .TextBoxv5.Text), 0)
            rawSupplyAirflow = (sensibleLoad + ventilationLighting) / (0.335 * supplyTempDeltaValue);
          } else {
            // 난방 모드: ASHRAE 공식 - 급기량 = 난방 부하 / (0.335 * 급기온도차)
            // 급기(△T) 의미: 급기온도 - 실내온도 (사용자는 양수로 입력)
            // ρ × cp / 3600 = 1.2 × 1.006 / 3.6 = 0.335
            rawSupplyAirflow = heatingLoadValue / (0.335 * supplyTempDeltaValue);
          }

          // VBA Labelv1 설정 로직 적용: OB5=True OR OB6=True일 때 RoundUp 사용
          if (rawSupplyAirflow >= 100) {
            // VBA: Application.RoundUp(SAvol, -2) - 100의 자리에서 올림
            calculatedSupplyAirflow = PsychrometricCalculator.vbaRoundUp(rawSupplyAirflow, -2);
          } else if (rawSupplyAirflow < 100) {
            // VBA: Application.RoundUp(SAvol, -1) - 10의 자리에서 올림
            calculatedSupplyAirflow = PsychrometricCalculator.vbaRoundUp(rawSupplyAirflow, -1);
          } else {
            calculatedSupplyAirflow = rawSupplyAirflow;
          }
          break;
        }
      case 'direct': // B: 직접입력 - VBA OB4
      default:
        {
          // 직접입력 모드에서는 directSupplyAirflow 필드 사용
          const directInputValue = parseFloat(directSupplyAirflow);
          // 직접입력 값이 없거나 0이면 급기량을 0으로 설정 (표준 모드와 동일하게)
          calculatedSupplyAirflow = !isNaN(directInputValue) && directInputValue > 0 ? directInputValue : 0;
          break;
        }
    }

    // 외기량 계산 (VBA 원본 기준에 따라)
    let baseOutdoorAirflow = 8700; // 기본값

    switch (outdoorAirSelectionCriteria) {
      case 'occupancy': // A: 인원수 기준 (m³/h/인)
        {
          const occupants = parseFloat(occupantCount) || 0;
          const airflowPerson = parseFloat(airflowPerPerson) || 25; // m³/h/인
          baseOutdoorAirflow = occupants * airflowPerson;
          break;
        }
      case 'direct': // B: 직접입력 (m³/h)
       {
          const directInputValue = parseFloat(directOutdoorAirflow);
          baseOutdoorAirflow = !isNaN(directInputValue) && directInputValue > 0 ? directInputValue : 8700;
          break;
       }
      case 'standard': // C: 급기량과 동일 (표준)
      default:
        baseOutdoorAirflow = calculatedSupplyAirflow;
        break;
    }

    // VBA 외기량 결정 로직: OAvol = max(입력 외기량, 배기량+가압량)
    const exhaustAirflowValue = parseFloat(exhaustAirflow) || 0;
    const pressurizationAirflowValue = parseFloat(pressurizationAirflow) || 0;
    const minRequiredOutdoorAirflow = exhaustAirflowValue + pressurizationAirflowValue;

    const calculatedOutdoorAirflow = Math.max(baseOutdoorAirflow, minRequiredOutdoorAirflow);

    // VBA 환기량 계산 로직: RAvol = 급기량 - 배기량 - 가압량
    const calculatedReturnAirflow = Math.max(0, calculatedSupplyAirflow - exhaustAirflowValue - pressurizationAirflowValue);

    const result = {
      supplyAirflow: calculatedSupplyAirflow,
      outdoorAirflow: calculatedOutdoorAirflow,
      returnAirflow: calculatedReturnAirflow,
      // 추가 정보
      exhaustAirflow: exhaustAirflowValue,
      pressurizationAirflow: pressurizationAirflowValue,
      minRequiredOutdoorAirflow,
      baseOutdoorAirflow, // 자동 조정 판단을 위해 추가
      wasAdjusted: calculatedOutdoorAirflow > baseOutdoorAirflow // 실제로 조정되었는지 판단
    };

    return result;
  }, [
    supplyAirSelectionCriteria,
    outdoorAirSelectionCriteria,
    sensibleCoolingLoad,
    latentCoolingLoad,
    heatingLoad,
    supplyTempDelta,
    ventilationLightingLoad,
    occupantCount,
    airflowPerPerson,
    directOutdoorAirflow,
    exhaustAirflow,
    pressurizationAirflow,
    directSupplyAirflow,
    fanDataTimestamp,
    airflowCalculationTimestamp
  ]);

  // 현재 선택된 외기조건 가져오기 (ahuCalculationResults보다 먼저 정의 필요)
  const getCurrentOutdoorCondition = (code: string): OutdoorCondition | undefined => {
    const baseCondition = OUTDOOR_CONDITIONS.find(c => c.code === code)
    if (!baseCondition) return undefined

    // 프로젝트가 있고, 선택된 코드가 프로젝트 코드와 일치하며, 실제 온습도 값이 있는 경우
    if (currentProject &&
      currentProject.outdoorConditionCode === code &&
      currentProject.summerOutdoorTemp &&
      currentProject.winterOutdoorTemp) {
      // 프로젝트의 실제 온습도 값으로 업데이트
      return {
        ...baseCondition,
        summer: {
          temperature: parseFloat(currentProject.summerOutdoorTemp),
          humidity: parseFloat(currentProject.summerOutdoorHumidity || baseCondition.summer.humidity.toString())
        },
        winter: {
          temperature: parseFloat(currentProject.winterOutdoorTemp),
          humidity: parseFloat(currentProject.winterOutdoorHumidity || baseCondition.winter.humidity.toString())
        }
      }
    }

    // 프로젝트 값이 없거나 다른 코드인 경우 기본값 사용
    return baseCondition
  }

  // AHU 계산에 필요한 필드들은 useMemo 내부에서 호출



  // AHU 공기 상태 계산
  const ahuCalculationResults = useMemo((): any => {
    if (!currentProject) return null;

    // 폼 데이터를 한 번만 가져오기
    const formData = watch();


    // 외기설계조건 선택 (프로젝트 추가 시 선택한 A, B 등의 조건)
    const outdoorDesignCode = formData.outdoorDesignCondition || currentProject.outdoorConditionCode || 'A';
    const selectedCondition = OUTDOOR_CONDITIONS.find(condition =>
      condition.code === outdoorDesignCode
    );

    // getCurrentOutdoorCondition은 selectedCondition과 동일한 데이터를 반환 (호환성 유지)
    const outdoorCondition = getCurrentOutdoorCondition(outdoorDesignCode);

    if (!outdoorCondition || !selectedCondition) {
      return null;
    }

    try {

      const inputs: AHUInputs = {
        // 실내 조건 (프로젝트에서)
        roomTemp: parseFloat(formData.coolingRoomTemp) || 26, // 냉방시 실내온도 (℃)
        roomHumidity: parseFloat(formData.coolingRoomHumidity) || 55, // 냉방시 실내 상대습도 (%)

        // 외기 조건 (장비 기준 탭 표시값과 동일하게 getCurrentOutdoorCondition 사용)
        outdoorTemp: outdoorCondition.summer.temperature,
        outdoorHumidity: (() => {
          // VBA designcode.bas 로직 구현: 시간별 외기조건 동적 계산
          const baseTemp = outdoorCondition.summer.temperature; // k3 (외기온도)
          const baseHumidity = outdoorCondition.summer.humidity; // l3 (외기 상대습도)
          const dailyRange = (() => {
            // 프로젝트에서 일교차 값 우선 사용, 없으면 외기조건 기본값 사용
            if ((currentProject as any)?.summerDailyRange) {
              return parseFloat((currentProject as any).summerDailyRange);
            }
            return selectedCondition.summer.dailyRange; // o3 (일교차) - 사용자가 선택한 외기설계조건
          })();
          
          // 외기 적용 시각 결정 (표준 적용 또는 피크 시각 적용)
          let effectiveHour: number;
          if ((formData.outdoorTimeApplication || 'standard') === 'standard') {
            effectiveHour = 15; // 표준 적용: 15시 (오후 3시)
          } else {
            effectiveHour = parseInt(formData.outdoorApplicationTime) || 15; // 피크 시각 적용: 사용자 입력 시각
          }
          
          // VBA designcode.bas의 시간별 외기조건 계산 로직 적용
          const altitude = currentProject?.altitude ? parseFloat(currentProject.altitude) : 0;
          const hourlyCondition = calculateHourlyOutdoorCondition(
            effectiveHour, 
            baseTemp, 
            baseHumidity, 
            dailyRange, 
            altitude // 프로젝트에서 고도 값 가져오기
          );
          
          
          return hourlyCondition.humidity;
        })(),
        altitude: currentProject?.altitude ? parseFloat(currentProject.altitude) : 0, // 고도 (m) - ASHRAE 계산용
        
        // ASHRAE 시간별 온도 계산용
        currentHour: parseInt(formData.outdoorApplicationTime) || 15, // 냉방 외기 적용시각 (사용자 입력)
        peakTemperature: outdoorCondition.summer.temperature, // k3 값과 동일
        dailyRange: (() => {
          // 프로젝트에서 일교차 값 우선 사용, 없으면 외기조건 기본값 사용
          if ((currentProject as any)?.summerDailyRange) {
            return parseFloat((currentProject as any).summerDailyRange);
          }
          return selectedCondition.summer.dailyRange; // VBA rmbook1.Range("o3") 값 - 사용자가 선택한 외기설계조건
        })(),
        
        // 외기 적용 시각 설정
        outdoorTimeApplication: formData.outdoorTimeApplication || 'standard', // 표준 적용 또는 사용자 설정
        outdoorApplicationTime: formData.outdoorApplicationTime || '15', // 사용자 설정 시각 (시)
        
        // 풍량 조건 - VBA Labelv1은 실 전체 급기량 (formData.supplyAirflow)
        supplyAirflow: parseFloat(formData.supplyAirflow) || 18900, // VBA 기대값으로 수정
        outdoorAirflow: calculatedAirflows.outdoorAirflow,
        
        // 공간 정보 (폼에서 가져옴)
        conditionedArea: parseFloat(formData.conditionedArea) || 0, // 공조면적 (m²)
        ceilingHeight: parseFloat(formData.ceilingHeight) || 2.7, // 층고 (m)
        
        // 부하 조건 (폼에서 가져옴)
        sensibleHeat: parseFloat(formData.sensibleCoolingLoad) || 0, // 냉방 현열부하 (W)
        latentHeat: parseFloat(formData.latentCoolingLoad) || 0, // 냉방 잠열부하 (W)
        heatingLoad: parseFloat(formData.heatingLoad) || 0, // 난방 부하 (W)

        // 난방 실내 조건
        heatingRoomTemp: (() => {
          const val = parseFloat(formData.heatingRoomTemp);
          return isNaN(val) ? 20 : val;
        })(),
        heatingRoomHumidity: (() => {
          const val = parseFloat(formData.heatingRoomHumidity);
          return isNaN(val) ? 40 : val;
        })(),

        // 난방 외기 조건 (장비 기준 탭 표시값과 동일하게 getCurrentOutdoorCondition 사용)
        heatingOutdoorTemp: (() => {
          const temp = outdoorCondition.winter.temperature;
          return temp;
        })(),
        heatingOutdoorHumidity: (() => {
          const humidity = outdoorCondition.winter.humidity;
          return humidity;
        })(),
        
        // RA Lighting Load 설정
        raLightingLoadMode: formData.raLightingLoadMode,
        ventilationLightingLoad: parseFloat(formData.ventilationLightingLoad) || 0, // 환기 조명부하 (W)
        
        
        // 환기량 (RA 온도상승 계산용) - 사용자 입력값 사용
        returnAirflow: parseFloat(formData.ventilationAirflow) || calculatedAirflows.returnAirflow, // 사용자 입력 우선, 없으면 계산값
        
        
        // 장비 조건 (VBA 매핑에 맞게 수정)
        coolingCoilTemp: parseFloat(formData.supplyTempDelta) || 10, // VBA CCdb는 급기 온도차 (△T) - TextBoxv5
        supplyTempDelta: parseFloat(formData.supplyTempDelta) || 10, // 급기 온도차 - 에러 메시지 표시용
        saFanTemp: parseFloat(formData.saFanTempDelta) || 2, // SA 팬 온도상승 (VBA 기대값에 맞게 조정)
        rhReheatTemp: parseFloat(formData.rhReheatTemp) || 0, // VBA RHte (재열 온도상승)
        supplyAirTemp: parseFloat((formData as any).supplyAirTemp) || 18, // 급기 온도 (설정값)
        preheatingTemp: (() => {
          // 예열코일 온도상승 계산 (인라인)
          if (formData.preheatingCoilType === 'None' || !formData.preheatingHeatCapacity) {
            return 0;
          }

          const heatCapacity = parseFloat(formData.preheatingHeatCapacity) || 0; // Wh/kg
          const safetyFactor = parseFloat(formData.preheatingSafetyFactor) || 10; // %
          const supplyAirflow = parseFloat(formData.supplyAirflow) || 18900; // CMH (실제 입력값 사용)

          // 총 열용량 계산 (안전율 적용)
          const totalHeatCapacity = heatCapacity * (1 + safetyFactor / 100); // Wh/kg

          // 온도상승 계산: Q = m × Cp × ΔT (단위 수정)
          const airMassFlow = (supplyAirflow * 1.2) / 3600; // kg/s (공기밀도 1.2 kg/m³)
          const airSpecificHeat = 1005; // J/kg·K (공기 비열)
          
          // 열용량이 Wh/kg라면 실제로는 W/kg 단위로 보임 (VBA 확인 필요)
          // 임시로 더 작은 값으로 계산해보기
          const heatInput = totalHeatCapacity; // W (단위 조정)
          const tempRise = heatInput / (airMassFlow * airSpecificHeat); // K


          return tempRise;
        })(), // 예열코일 온도상승 계산


        // 코일 타입 정보 추가
        preheatingCoilType: formData.preheatingCoilType,
        reheatingCoilType: formData.reheatingCoilType,
        heatingCoilType: formData.heatingCoilType,

        // 코일 정보 (ADP 검증용 - VBA ahucode3.bas:1440)
        coolingCoilType: formData.coolingCoilType || 'None', // 냉각코일 타입: 'None' | 'Water' | 'DX'
        coolingInletTemp: parseFloat(formData.coolingInletTemp) || 7, // 냉수 입구 온도 (VBA TextBox10)
        coolingOutletTemp: parseFloat(formData.coolingOutletTemp) || 12, // 냉수 출구 온도 (VBA TextBox11)

        // 가열 코일 온도 정보
        heatingInletTemp: parseFloat(formData.heatingInletTemp) || 80, // 난방 코일 입구 온도
        heatingOutletTemp: parseFloat(formData.heatingOutletTemp) || 70, // 난방 코일 출구 온도
        preheatingInletTemp: parseFloat(formData.preheatingInletTemp) || 80, // 예열 코일 입구 온도
        preheatingOutletTemp: parseFloat(formData.preheatingOutletTemp) || 70, // 예열 코일 출구 온도
        reheatingInletTemp: parseFloat(formData.reheatingInletTemp) || 60, // 재열 코일 입구 온도
        reheatingOutletTemp: parseFloat(formData.reheatingOutletTemp) || 50, // 재열 코일 출구 온도

        // 팬 설정 (동적 SAte 계산용) - VBA 기본값은 팬 없음 (SAte = 0)
        hasFan: formData.sfLoadEnabled === true, // SF부하 적용(%) 체크박스 상태
        fanType: formData.supplyFan?.type || 'Draw(I)', // VBA 기본값 Draw(I)
        fanEfficiency: (() => {
          // Fan Selection에서 Supply Fan 효율 읽어오기
          if (!state.currentAHUId) {
            return 65;
          }

          try {
            // 다양한 키 패턴 시도
            const possibleKeys = [
              `fan-selection-form-data-${state.currentAHUId}`,
              `fan-selection-form-data`,
              `KDCSCOKR_FANSELECTION_FORM_${state.currentAHUId}`
            ];
            
            for (const key of possibleKeys) {
              const data = localStorage.getItem(key);
              if (data) {
                const fanSelectionData = JSON.parse(data);
                const efficiency = parseFloat(fanSelectionData.supplyFan?.efficiency);
                if (efficiency > 0) {
                  return efficiency;
                }
              }
            }
          } catch (error) {
            // Error handled silently
          }
          return 65;
        })(),
        fanPressure: (() => {
          // Fan Selection SF S.P Total 값 읽어오기
          if (!state.currentAHUId) {
            // console.error('❌ [AHUSelectionForm] fanPressure: currentAHUId 없음 - Fan Selection 탭에서 먼저 데이터를 입력하세요');
            return 0;
          }

          try {
            const possibleKeys = [
              `fan-selection-form-data-${state.currentAHUId}`,
              `fan-selection-form-data`,
              `KDCSCOKR_FANSELECTION_FORM_${state.currentAHUId}`
            ];

          

            for (const key of possibleKeys) {
              const data = localStorage.getItem(key);
             
              if (data) {
                const fanSelectionData = JSON.parse(data);

                // 1순위: 계산된 sfStaticPressure.total
                let pressure = parseFloat(fanSelectionData.sfStaticPressure?.total);
                

                if (pressure > 0) {
                  //console.log(`✅ [fanPressure] 로드 성공 (계산값): ${pressure} Pa (키: ${key})`);
                  return pressure;
                }

                // 2순위: supplyFan.staticPressure (사용자 입력값)
                pressure = parseFloat(fanSelectionData.supplyFan?.staticPressure);
                //console.log(`🔍 [fanPressure] ${key} supplyFan.staticPressure:`, pressure);

                if (pressure > 0) {
                  //console.log(`✅ [fanPressure] 로드 성공 (입력값): ${pressure} Pa (키: ${key})`);
                  return pressure;
                }

                // 3순위: AHU Selection Form의 formData에서 읽기
                const formDataPressure = parseFloat(formData.supplyFan?.staticPressure);
                //console.log(`🔍 [fanPressure] formData.supplyFan.staticPressure:`, formDataPressure);

                if (formDataPressure > 0) {
                  //console.log(`✅ [fanPressure] 로드 성공 (formData): ${formDataPressure} Pa`);
                  return formDataPressure;
                }
              }
            }
          } catch (error) {
            console.error('❌ [AHUSelectionForm] fanPressure 로드 실패:', error);
            return 0;
          }
          // FAN 선정 탭에서 데이터를 아직 입력하지 않은 경우 (정상적인 상황)
          // console.warn('⚠️ [AHUSelectionForm] fanPressure 미입력 - Fan Selection 탭에서 정압 데이터 입력 필요');
          return 0;
        })(), 
        motorEfficiency: parseFloat(formData.supplyFan?.motorEfficiency) || 85, // 모터 효율 (%)
        motorSafetyFactor: (() => {
          // Fan Selection에서 Supply Fan 안전율 읽어오기
          if (!state.currentAHUId) {
            return 15;
          }

          try {
            const possibleKeys = [
              `fan-selection-form-data-${state.currentAHUId}`,
              `fan-selection-form-data`,
              `KDCSCOKR_FANSELECTION_FORM_${state.currentAHUId}`
            ];
            
            for (const key of possibleKeys) {
              const data = localStorage.getItem(key);
              if (data) {
                const fanSelectionData = JSON.parse(data);
                const safetyFactor = parseFloat(fanSelectionData.supplyFan?.safetyFactor);
                if (safetyFactor > 0) {
                  return safetyFactor;
                }
              }
            }
          } catch (error) {
            // Error handled silently
          }
          return 15;
        })(), // 모터 안전율 (%)
        sfLoadPercentage: parseFloat(formData.sfLoadPercentage) || 100, // 급기팬 부하 온도상승 적용(%)
        rfLoadPercentage: parseFloat(formData.rfLoadPercentage) || 100, // 환기팬 부하 온도상승 적용(%)

        // 코일 유무
        hasCoolingCoil: formData.coolingCoilType !== 'None',
        hasHeatingCoil: formData.heatingCoilType !== 'None'
      };

      
      const result = AHUCalculator.calculate(inputs);
      // calculatedAirflows의 outdoorAirflow를 결과에 추가 (출력 시트에서 사용)
      // sfLoadPercentage, rfLoadPercentage도 결과에 포함 (출력 시트 연동)
      const finalResult = {
        ...result,
        outdoorAirflow: calculatedAirflows.outdoorAirflow,
        sfLoadPercentage: inputs.sfLoadPercentage,
        rfLoadPercentage: inputs.rfLoadPercentage
      };
      //console.log("🎯 [useMemo return] sfLoadPercentage:", finalResult.sfLoadPercentage, "| rfLoadPercentage:", finalResult.rfLoadPercentage);
      return finalResult;
    } catch (error) {
      return null;
    }
  }, [
    currentProject,
    calculatedAirflows,
    fanDataTimestamp,
    state.currentAHUId
  ])
  
  useEffect(() => {
    // 냉방, 예열, 가열, 재열이 모두 None이면 에러 (VBA 라인 65-68 로직)
    if (coolingCoilType === 'None' && preheatingCoilType === 'None' &&
      heatingCoilType === 'None' && reheatingCoilType === 'None') {
      setCoilValidationError('냉방, 예열, 가열, 재열 코일 중 적어도 하나는 선택해야 합니다.')
    } else {
      setCoilValidationError('')
    }
  }, [coolingCoilType, preheatingCoilType, heatingCoilType, reheatingCoilType])

  // 예열 코일이 Steam으로 선택될 때 기본값 설정
  useEffect(() => {
    if (preheatingCoilType === 'Steam') {
      setValue('preheatingHeatCapacity', '620') // 열용량 620 Wh/kg
      setValue('preheatingSafetyFactor', '10')   // 안전율 10%
      setValue('preheatingOutletTemp', '10')     // 급기 온도 10℃
    }
  }, [preheatingCoilType])

  // 재열 코일 타입이 None으로 변경되면 온도상승 초기화
  useEffect(() => {
    if (reheatingCoilType === 'None') {
      //console.log(`⚠️ [rhReheatTemp] reheatingCoilType이 None으로 변경되어 rhReheatTemp를 0으로 초기화합니다.`);
      setValue('rhReheatTemp', '0')
    }
  }, [reheatingCoilType])

  // AHU 계산 결과를 GlobalState에 저장 (무한 루프 방지를 위한 별도 useEffect)
  // 현재는 계산 결과를 GlobalState에 저장하지 않음 (무한 루프 방지)

  // AHU 폼 데이터 로딩 함수 (디버깅 강화)
  const loadAHUFormData = (ahuData: any) => {
    // Layer 1: 기본 정보 (현재 값과 다를 때만 설정)
    try {
      const currentValues = watch();
      if (currentValues.ahuNo !== ahuData.ahuNo) {
        setValue('ahuNo', ahuData.ahuNo, { shouldDirty: false });
      }
      if (currentValues.systemName !== ahuData.systemName) {
        setValue('systemName', ahuData.systemName, { shouldDirty: false });
      }
      if (currentValues.quantity !== ahuData.quantity) {
        setValue('quantity', ahuData.quantity, { shouldDirty: false });
      }
      if (currentValues.location !== ahuData.location) {
        setValue('location', ahuData.location, { shouldDirty: false });
      }
    } catch (error) {
      // Error setting basic values
    }

    // Layer 2: 저장된 AHU 데이터 (현재 값과 다를 때만 설정)
    if (ahuData.formData) {
      const currentValues = watch();
      Object.keys(ahuData.formData).forEach(key => {
        let value = ahuData.formData[key];

        // 급기량/외기량 선정 기준이 빈 문자열이면 기본값 적용
        if (key === 'supplyAirSelectionCriteria' && (!value || value === '')) {
          value = 'standard';
        }
        if (key === 'outdoorAirSelectionCriteria' && (!value || value === '')) {
          value = 'occupancy';
        }

       

        if (value !== undefined && key !== 'saFanTempDelta' && currentValues[key] !== value) {
          setValue(key as any, value, { shouldDirty: false });
        }
      });
    }

    // Layer 3: 로컬 스토리지에서 임시 데이터 (가장 우선순위 높음)
    const localStorageKey = `KDCSCOKR_AHUSELECTION_FORM_${ahuData.id}`;

    // 모든 localStorage 키 확인
    const allKeys = Object.keys(localStorage).filter(key => key.startsWith('KDCSCOKR_AHUSELECTION_FORM_'));

    const savedData = localStorage.getItem(localStorageKey);
    if (savedData) {
      try {
        // 이전 필드명(sensibleHeatLoad/latentHeatLoad)으로 저장된 per-AHU 캐시 호환 처리
        const parsedData = normalizeAHUFormCoolingFields(JSON.parse(savedData));

        // 데이터 일치성 확인
        if (parsedData.ahuNo && parsedData.ahuNo !== ahuData.ahuNo) {
          localStorage.removeItem(localStorageKey);
          // formData에서만 로드하고 localStorage는 무시
          return;
        }

        const currentValues = watch();
        Object.keys(parsedData).forEach(key => {
          let value = parsedData[key];

          // 급기량/외기량 선정 기준이 빈 문자열이면 기본값 적용
          if (key === 'supplyAirSelectionCriteria' && (!value || value === '')) {
            value = 'standard';
          }
          if (key === 'outdoorAirSelectionCriteria' && (!value || value === '')) {
            value = 'occupancy';
          }

         

          if (value !== undefined && key !== 'saFanTempDelta' && currentValues[key] !== value) {
            setValue(key as any, value, { shouldDirty: false });
          }
        });
      } catch (e) {
        localStorage.removeItem(localStorageKey);
      }
    }
  };

  // 데이터 로드 및 복원 로직 (무한 루프 방지)
  useEffect(() => {
    // 글로벌 상태가 로드되지 않았으면 실행하지 않음
    if (!state.isLoaded) {
      return;
    }

    // Layer 1: 프로젝트 기본값 설정 (외기설계 조건)
    if (currentProject && currentProject.outdoorConditionCode) {
      setValue('outdoorDesignCondition', currentProject.outdoorConditionCode, { shouldDirty: false });
    }

    // Layer 2 & 3: AHU 데이터 로딩 - currentAHUId가 있을 때만
    if (state.currentAHUId) {
      const ahuData = state.ahus.find(ahu => ahu.id === state.currentAHUId);
      if (ahuData) {
        loadAHUFormData(ahuData);
      }
    }
  }, [currentProject?.outdoorConditionCode, state.currentAHUId, state.isLoaded]);

  // 급기량 선정 기준 기본값 보장 (한 번만 실행)
  useEffect(() => {
    const currentValue = watch('supplyAirSelectionCriteria');
    if (!currentValue && state.isLoaded) {
      setValue('supplyAirSelectionCriteria', 'standard', { shouldDirty: false });
    }
  }, [state.isLoaded]); // 의존성 배열을 제한하여 무한 루프 방지

  // calculationResults만 업데이트 (psychroChartData는 AirflowSelectionTab에서만 계산)
  const prevCalculationResultsRef = useRef<any>(null);
  const prevCurrentAHUIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (ahuCalculationResults && state.currentAHUId) {
      const ahuToUpdate = state.ahus.find(ahu => ahu.id === state.currentAHUId);

      
      // AHU ID가 변경되었거나, calculationResults가 실제로 변경된 경우에만 업데이트
      const ahuIdChanged = prevCurrentAHUIdRef.current !== state.currentAHUId;

      // Redux store와 비교하여 변경 여부 판단 (prevRef가 아니라 실제 Redux 값과 비교)
      const reduxHasDifferentValues =
        ahuToUpdate?.calculationResults?.sfLoadPercentage !== ahuCalculationResults.sfLoadPercentage ||
        ahuToUpdate?.calculationResults?.rfLoadPercentage !== ahuCalculationResults.rfLoadPercentage;

      // sfLoadPercentage, rfLoadPercentage 변경 체크 추가
      const loadPercentageChanged =
        prevCalculationResultsRef.current?.sfLoadPercentage !== ahuCalculationResults.sfLoadPercentage ||
        prevCalculationResultsRef.current?.rfLoadPercentage !== ahuCalculationResults.rfLoadPercentage;

      const resultsChanged =
        JSON.stringify(prevCalculationResultsRef.current) !== JSON.stringify(ahuCalculationResults) ||
        loadPercentageChanged;

      // Redux store와 다르거나, AHU가 변경되었거나, 계산 결과가 변경된 경우 업데이트
      const shouldUpdate = ahuIdChanged || resultsChanged || reduxHasDifferentValues;

      //console.log('🔄 [useEffect] ahuIdChanged:', ahuIdChanged, '| resultsChanged:', resultsChanged, '| reduxHasDifferentValues:', reduxHasDifferentValues, '| shouldUpdate:', shouldUpdate);

      if (shouldUpdate && ahuToUpdate) {
        //console.log('✅ [useEffect] UPDATE_AHU 디스패치 - AHU ID:', state.currentAHUId);
        const updatedAHU: AHUData = {
          ...ahuToUpdate,
          calculationResults: ahuCalculationResults,
          updatedAt: new Date().toISOString()
        };
        dispatch({ type: 'UPDATE_AHU', payload: updatedAHU });

        // 이전 값 업데이트
        prevCalculationResultsRef.current = ahuCalculationResults;
        prevCurrentAHUIdRef.current = state.currentAHUId;
      } else if (shouldUpdate && !ahuToUpdate) {
        console.error('❌ [useEffect] ahuToUpdate를 찾을 수 없음! currentAHUId:', state.currentAHUId);
      } else {
        //console.log('⏭️ [useEffect] 변경사항 없음 - 업데이트 스킵');
      }
    }
  }, [ahuCalculationResults, state.currentAHUId, state.ahus, dispatch]);

  // AHU 번호 중복 확인 함수 (단방향 동기화 - 검증만 수행)
  const validateAHUNumber = (value: string) => {
    if (state.currentAHUId && value !== '') {
      const currentProjectAHUs = state.ahus.filter(ahu => ahu.projectId === state.currentProjectId)
      const duplicateAHU = currentProjectAHUs.find(ahu =>
        ahu.id !== state.currentAHUId && ahu.ahuNo === value
      )

      if (duplicateAHU) {
        // 중복된 AHU 번호가 있는 경우 경고 표시
        setDuplicateWarning({
          open: true,
          message: `이미 사용 중인 AHU 번호입니다: ${value}. 다른 번호를 사용해주세요.`
        })
        return false
      }

      return true
    }
    return true
  }

  // AHU 번호는 자동 저장 시스템을 통해 처리됨

  // localStorage 정리 함수 (개발/디버깅용)
  const clearAHULocalStorage = () => {
    const allKeys = Object.keys(localStorage).filter(key => key.startsWith('KDCSCOKR_AHUSELECTION_FORM_'));
    allKeys.forEach(key => localStorage.removeItem(key));
  }

  // 전역 함수로 노출 (개발자 콘솔에서 사용 가능)
  if (typeof window !== 'undefined') {
    (window as any).clearAHULocalStorage = clearAHULocalStorage;
  }

  // 프로젝트 파일 저장/가져오기 함수들
  const exportProjectToFile = (project: any, filename?: string) => {
    if (!project) {
      alert('저장할 프로젝트가 선택되지 않았습니다.')
      return
    }

    // 프로젝트와 관련된 모든 AHU 데이터 포함
    const projectAHUs = state.ahus.filter(ahu => ahu.projectId === project.id)

    const exportData = {
      project: project,
      ahus: projectAHUs,
      exportDate: new Date().toISOString(),
      version: '1.0'
    }

    const jsonString = JSON.stringify(exportData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename || `${project.projectName}_${new Date().toISOString().split('T')[0]}.dcs`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const importProjectFromFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const result = e.target?.result as string
        const importData = JSON.parse(result)

        // 데이터 유효성 검증
        if (!importData.project || !importData.ahus) {
          throw new Error('올바르지 않은 프로젝트 파일 형식입니다.')
        }

        // 프로젝트 ID 중복 방지를 위해 새 ID 생성
        const newProjectId = generateUUID()
        const originalProjectId = importData.project.id

        // 프로젝트 데이터 업데이트
        const newProject = {
          ...importData.project,
          id: newProjectId,
          projectName: `${importData.project.projectName}_가져옴`,
          createdAt: new Date().toLocaleDateString()
        }

        // AHU 데이터 업데이트 (새 프로젝트 ID와 새 AHU ID 할당)
        // 이전 필드명(sensibleHeatLoad/latentHeatLoad) → 신규 필드명(sensibleCoolingLoad/latentCoolingLoad) 호환 처리
        const newAHUs = importData.ahus.map((ahu: any) => ({
          ...ahu,
          id: generateUUID(),
          projectId: newProjectId,
          formData: normalizeAHUFormCoolingFields(ahu.formData) || ahu.formData,
          updatedAt: new Date().toISOString()
        }))

        // 글로벌 상태에 추가
        dispatch({ type: 'ADD_PROJECT', payload: newProject })
        newAHUs.forEach((ahu: any) => {
          dispatch({ type: 'ADD_AHU', payload: ahu })
        })

        // 새로 가져온 프로젝트로 전환
        dispatch({ type: 'SET_CURRENT_PROJECT', payload: newProjectId })
        if (newAHUs.length > 0) {
          dispatch({ type: 'SET_CURRENT_AHU', payload: newAHUs[0].id })
        }

        alert(`프로젝트 "${newProject.projectName}"가 성공적으로 가져와졌습니다.\n- 프로젝트: 1개\n- AHU: ${newAHUs.length}개`)

      } catch (error) {
        alert(`프로젝트 파일을 가져오는 중 오류가 발생했습니다:\n${error}`)
      }
    }
    reader.readAsText(file)
  }

  const saveCurrentProject = () => {
    if (!currentProject) {
      alert('저장할 프로젝트가 없습니다.')
      return
    }
    exportProjectToFile(currentProject)
  }

  const saveProjectAs = () => {
    if (!currentProject) {
      alert('저장할 프로젝트가 없습니다.')
      return
    }

    const filename = prompt('파일명을 입력하세요 (.dcs 확장자는 자동으로 추가됩니다):', currentProject.projectName)
    if (filename && filename.trim()) {
      const safeFilename = filename.trim().replace(/[^a-zA-Z0-9가-힣_-]/g, '_')
      exportProjectToFile(currentProject, `${safeFilename}.dcs`)
    }
  }

  // 파일 메뉴 핸들러들
  const handleFileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation() // 프로젝트 정보 토글 방지
    setFileMenuAnchor(event.currentTarget)
  }

  const handleFileMenuClose = () => {
    setFileMenuAnchor(null)
  }

  const handleOpenFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
    handleFileMenuClose()
  }

  const handleProjectExport = () => {
    if (!currentProject) {
      alert('내보낼 프로젝트가 선택되지 않았습니다.')
      return
    }

    const safeFilename = currentProject.projectName
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/\s+/g, '_')
      .trim()

    exportProjectToFile(currentProject, `${safeFilename}.dcs`)
    handleFileMenuClose()
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.name.endsWith('.dcs') || file.name.endsWith('.json')) {
        importProjectFromFile(file)
      } else {
        alert('올바른 프로젝트 파일(.dcs 또는 .json)을 선택해주세요.')
      }
    }
    // input 값 초기화
    if (event.target) {
      event.target.value = ''
    }
  }

  // 초기화 핸들러
  const handleResetRequest = () => {
    handleFileMenuClose()
    setIsResetDialogOpen(true)
  }

  const handleResetWithSave = () => {
    // 현재 프로젝트 저장
    if (currentProject) {
      const safeFilename = currentProject.projectName
        .replace(/[<>:"/\\|?*]/g, '_')
        .replace(/\s+/g, '_')
        .trim()

      exportProjectToFile(currentProject, `${safeFilename}.dcs`)
    }

    // 다이얼로그 먼저 닫기
    setIsResetDialogOpen(false)

    // 초기화 실행
    setTimeout(() => {
      performReset()
    }, 100)
  }

  const handleResetWithoutSave = () => {
    // 다이얼로그 먼저 닫기
    setIsResetDialogOpen(false)

    // 초기화 실행
    setTimeout(() => {
      performReset()
    }, 100)
  }

  const performReset = () => {
    // 현재 프로젝트와 AHU 선택 해제
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: null })
    dispatch({ type: 'SET_CURRENT_AHU', payload: null })

    // 폼 초기화
    reset()

    // 프로젝트 관리 창 열기
    setTimeout(() => {
      setIsProjectManagementOpen(true)
    }, 100)
  }

  const handleResetCancel = () => {
    setIsResetDialogOpen(false)
  }

  // 폼 데이터 변경 시 자동 저장 (디바운스 적용)
  const formData = watch()
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const saveFormData = () => {
      if (state.currentAHUId && state.currentProjectId) {
        const currentFormData = watch();

        // 1. 로컬 스토리지에 현재 폼 데이터 저장
        try {
          const storageKey = `KDCSCOKR_AHUSELECTION_FORM_${state.currentAHUId}`;
          localStorage.setItem(storageKey, JSON.stringify(currentFormData));

          // 디버그: rhReheatTemp 저장 추적
          if (currentFormData.rhReheatTemp) {
            // console.log(`💾 [saveFormData] rhReheatTemp 저장:`, {
            //   ahuNo: currentFormData.ahuNo,
            //   rhReheatTemp: currentFormData.rhReheatTemp,
            //   reheatingCoilType: currentFormData.reheatingCoilType
            // });
          }
        } catch (e) {
          // Failed to save AHU form data to localStorage
        }

        // 1-1. 계산 결과를 localStorage에 저장
        if (ahuCalculationResults) {
          try {
            const calcStorageKey = 'calculationResults';
            const existingResults = JSON.parse(localStorage.getItem(calcStorageKey) || '{}');
            existingResults[state.currentAHUId!] = ahuCalculationResults.outdoorCoolingLoad;
            localStorage.setItem(calcStorageKey, JSON.stringify(existingResults));
            // console.log('💾 [계산 결과 저장]', {
            //   'ahuId': state.currentAHUId,
            //   'outdoorCoolingLoad': ahuCalculationResults.outdoorCoolingLoad,
            //   '전체 저장된 결과': existingResults
            // });
          } catch (e) {
            console.error('❌ 계산 결과 저장 실패:', e);
          }
        }

        // 2. 전역 상태에 업데이트
        const ahuToUpdate = state.ahus.find(ahu => ahu.id === state.currentAHUId)
        if (ahuToUpdate) {
          // psychroChartData는 별도의 useEffect (1466번 줄)에서 업데이트됨
          // 여기서는 formData만 업데이트

          const updatedAHU: AHUData = {
            ...ahuToUpdate,
            ahuNo: currentFormData.ahuNo || ahuToUpdate.ahuNo, // localStorage 문제 해결로 다시 활성화
            systemName: currentFormData.systemName || ahuToUpdate.systemName,
            quantity: currentFormData.quantity || ahuToUpdate.quantity,
            location: currentFormData.location || ahuToUpdate.location,
            formData: currentFormData as any,
            calculatedAirflows: calculatedAirflows, // 풍량 계산 결과 저장 (OUTPUT SHEETS에서 사용)
            // psychroChartData는 별도의 useEffect (1466번 줄)에서 업데이트됨
            updatedAt: new Date().toISOString()
          }
          dispatch({ type: 'UPDATE_AHU', payload: updatedAHU })
        }
      }
    };

    // 폼 변경 구독
    const subscription = watch(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // 디바운스 적용 (1000ms) - 무한 렌더링 방지
      timeoutId = setTimeout(() => {
        // 데이터 로딩 중이 아닐 때만 저장
        if (state.isLoaded) {
          saveFormData();
        }
      }, 1000);
    });

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      subscription.unsubscribe();
    }
  }, [state.currentAHUId, state.currentProjectId, state.isLoaded]) // formData 의존성 제거

  // 페이지 새로고침/종료 전 데이터 즉시 저장
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (state.currentAHUId && state.currentProjectId) {
        try {
          const currentFormData = watch();
          localStorage.setItem(`KDCSCOKR_AHUSELECTION_FORM_${state.currentAHUId}`, JSON.stringify(currentFormData));
        } catch (e) {
          // Failed to save AHU form data on beforeunload
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // 이벤트 리스너 제거 시에도 즉시 저장
      handleBeforeUnload();
    };
  }, [state.currentAHUId, state.currentProjectId]) // formData 의존성 제거

  // 냉온수 겸용 코일 로직
  useEffect(() => {
    if (commonCoil) {
      setValue('coolingCoilType', 'Water')
      setValue('reheatingCoilType', 'Water')
      // VBA에서는 겸용 코일시 예열은 영향받지 않음
    }
  }, [commonCoil])

  // SA Fan(△T) 자동 계산 및 설정 (VBA SAte 계산 로직)
  const supplyAirflow = watch('supplyAirflow');
  const sfLoadEnabled = watch('sfLoadEnabled');
  const prevSAteRef = useRef<string>('');

  useEffect(() => {
    if (ahuCalculationResults) {
      // ahuCalculations.ts의 동적계산 결과를 직접 사용 (fanRatio 44% 적용됨)
      const dynamicSAte = ahuCalculationResults.supplyFanTempRise || 0;

      const hasFan = sfLoadEnabled === true;
      const airflow = parseFloat(supplyAirflow) || 0;

      // 계산된 SAte 값이 있으면 사용 (팬 활성화 상태와 관계없이)
      let calculatedSAte = 0;
      if (airflow > 0 && dynamicSAte > 0) {
        calculatedSAte = dynamicSAte;
      }

      const newValue = calculatedSAte.toFixed(1);
      // 이전 값과 다를 때만 업데이트
      if (prevSAteRef.current !== newValue) {
        setValue('saFanTempDelta', newValue, { shouldDirty: false });
        prevSAteRef.current = newValue;
      }
    }
  }, [ahuCalculationResults, supplyAirflow, sfLoadEnabled, state.isLoaded]);

  // 급기량 자동 계산 결과를 폼에 반영
  const prevSupplyAirflowRef = useRef<string>('');

  useEffect(() => {
    if (supplyAirSelectionCriteria === 'standard') {
      const calculatedValue = Math.round(calculatedAirflows.supplyAirflow).toString();

      // 값이 실제로 다를 때만 업데이트 (무한 루프 방지)
      if (prevSupplyAirflowRef.current !== calculatedValue && calculatedAirflows.supplyAirflow > 0) {
        setValue('supplyAirflow', calculatedValue, {
          shouldDirty: false,
          shouldValidate: false
        });
        prevSupplyAirflowRef.current = calculatedValue;
      }
    }
  }, [supplyAirSelectionCriteria, calculatedAirflows.supplyAirflow, setValue]);

  // 환기량 자동 계산 및 업데이트 (급기량 - 배기량 - 가압량)
  const prevVentilationAirflowRef = useRef<string>('');

  useEffect(() => {
    const calculatedReturnAirflow = calculatedAirflows.returnAirflow;
    const newValue = Math.round(calculatedReturnAirflow).toString();

    // 값이 실제로 다를 때만 업데이트 (무한 루프 방지)
    if (prevVentilationAirflowRef.current !== newValue) {
      setValue('ventilationAirflow', newValue, {
        shouldDirty: false,
        shouldValidate: false
      });
      prevVentilationAirflowRef.current = newValue;
    }
  }, [
    calculatedAirflows.returnAirflow,
    setValue
  ]);

  // 활성 탭 상태를 로컬 스토리지에 저장
  useEffect(() => {
    try {
      const existingUIData = localStorage.getItem('KDCSCOKR_AHUSELECTION_UI');
      const parsedUIData = existingUIData ? JSON.parse(existingUIData) : {};
      parsedUIData.activeTab = activeTab.toString();
      localStorage.setItem('KDCSCOKR_AHUSELECTION_UI', JSON.stringify(parsedUIData));
    } catch (e) {
      // Failed to save active tab to localStorage
    }
  }, [activeTab])

  // 습공기 선도 데이터 저장 (렌더링 중 dispatch 방지)
  useEffect(() => {
    if (ahuCalculationResults && currentProjectAHUs.length > 0 && currentAHUIndex >= 0) {
      const currentAHU = currentProjectAHUs[currentAHUIndex];
      if (currentAHU) {
        // 습공기 선도 데이터 생성 로직은 별도 함수로 분리하는 것이 좋지만,
        // 현재는 간단히 AHU 계산 결과가 변경될 때만 저장
        // 실제 습공기 선도 데이터는 currentProjectAHUs useMemo에서 이미 계산됨
      }
    }
  }, [ahuCalculationResults, currentProjectAHUs, currentAHUIndex])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    
    // 팬 선정 탭에서 다른 탭으로 이동하거나 다른 탭에서 풍량 선정 탭으로 이동할 때 계산 갱신
    if ((activeTab === 2 && newValue !== 2) || (activeTab !== 1 && newValue === 1)) {
      debugLocalStorage() // 디버깅 정보 출력
      setFanDataTimestamp(Date.now())
      setAirflowCalculationTimestamp(Date.now())
    }
  }

  // 프로젝트 정보 토글 (현재는 프로젝트 관리 모달 열기)
  const toggleProjectInfo = () => {
    setIsProjectManagementOpen(true)
  }

  // 프로젝트 관리 함수들
  const handleCreateNewProject = (newProject: ProjectInfo) => {
    
    const projectData: ProjectInfo = {
      ...newProject,
      id: newProject.id || generateUUID()
    }
    
    dispatch({ type: 'ADD_PROJECT', payload: projectData })
    
    
    // 상태 업데이트 후 UI 업데이트를 위해 약간 지연    
    setTimeout(() => {
      setIsNewProjectDialogOpen(false)
      setIsProjectManagementOpen(false)
    }, 100)
  }

  const handleSelectProject = (projectId: string) => {
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: projectId })
    setIsProjectManagementOpen(false)
  }

  const handleViewProject = (project: ProjectInfo) => {
    setEditingProject(project)
    setIsViewOnlyMode(true)
    setIsNewProjectDialogOpen(true)
    setMenuAnchorEl(null)
  }

  const handleEditProject = (project: ProjectInfo) => {
    setEditingProject(project)
    setIsViewOnlyMode(false)
    setIsNewProjectDialogOpen(true)
    setMenuAnchorEl(null)
  }

  const handleUpdateProject = (updatedProject: ProjectInfo) => {
    if (editingProject) {
      const updatedProjectData: ProjectInfo = {
        id: editingProject.id,
        createdAt: editingProject.createdAt,
        projectName: updatedProject.projectName,
        buildingName: updatedProject.buildingName,
        location: updatedProject.location,
        buildingType: updatedProject.buildingType,
        totalFloors: updatedProject.totalFloors,
        basementFloors: updatedProject.basementFloors,
        buildingArea: updatedProject.buildingArea,
        altitude: updatedProject.altitude,
        outdoorConditionCode: updatedProject.outdoorConditionCode,
        summerOutdoorTemp: updatedProject.summerOutdoorTemp,
        summerOutdoorHumidity: updatedProject.summerOutdoorHumidity,
        summerDailyRange: updatedProject.summerDailyRange,
        winterOutdoorTemp: updatedProject.winterOutdoorTemp,
        winterOutdoorHumidity: updatedProject.winterOutdoorHumidity,
        summerIndoorTemp: updatedProject.summerIndoorTemp,
        summerIndoorHumidity: updatedProject.summerIndoorHumidity,
        winterIndoorTemp: updatedProject.winterIndoorTemp,
        winterIndoorHumidity: updatedProject.winterIndoorHumidity,
        updatedAt: new Date().toISOString()
      }
      dispatch({ type: 'UPDATE_PROJECT', payload: updatedProjectData })
      setEditingProject(null)
    }
    setIsNewProjectDialogOpen(false)
  }

  const handleDeleteProject = (projectId: string) => {
    if (state.projects.length <= 1) {
      alert('마지막 프로젝트는 삭제할 수 없습니다.')
      return
    }

    const projectToDelete = state.projects.find(p => p.id === projectId)
    if (window.confirm(`"${projectToDelete?.projectName}" 프로젝트를 삭제하시겠습니까?\n\n※ 프로젝트의 모든 데이터가 영구히 삭제됩니다.`)) {
      dispatch({ type: 'DELETE_PROJECT', payload: projectId })
    }
    setMenuAnchorEl(null)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setMenuAnchorEl(event.currentTarget)
    setSelectedProjectForMenu(projectId)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setSelectedProjectForMenu(null)
  }

  // 필터된 프로젝트 목록
  const filteredProjects = state.projects.filter(project =>
    project.projectName?.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
    project.buildingName?.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
    project.location?.toLowerCase().includes(projectSearchTerm.toLowerCase())
  )

  const onSubmit = (data: AHUFormData) => {
  }

  // AHU 관리 함수들
  const generateNextAHUNo = (): string => {
    const maxNum = Math.max(...currentProjectAHUs.map(ahu => parseInt(ahu.ahuNo.replace('AH-', '')) || 0), 0)
    return `AH-${String(maxNum + 1).padStart(3, '0')}`
  }

  const handlePreviousAHU = () => {
    if (currentAHUIndex > 0) {
      const prevAHU = currentProjectAHUs[currentAHUIndex - 1]
      dispatch({ type: 'SET_CURRENT_AHU', payload: prevAHU.id })
      loadAHUFormData(prevAHU)
    }
  }

  const handleNextAHU = () => {
    if (currentAHUIndex < currentProjectAHUs.length - 1) {
      const nextAHU = currentProjectAHUs[currentAHUIndex + 1]
      dispatch({ type: 'SET_CURRENT_AHU', payload: nextAHU.id })
      loadAHUFormData(nextAHU)
    }
  }

  // localStorage 데이터 정리 함수
  const cleanupOrphanedLocalStorageData = () => {
    const allKeys = Object.keys(localStorage).filter(key => key.startsWith('KDCSCOKR_AHUSELECTION_FORM_'));
    const validAHUIds = new Set(currentProjectAHUs.map(ahu => ahu.id));

    allKeys.forEach(key => {
      const ahuId = key.replace('KDCSCOKR_AHUSELECTION_FORM_', '');
      if (!validAHUIds.has(ahuId)) {
        localStorage.removeItem(key);
      }
    });
  }

  const handleSelectAHU = (index: number) => {
    const selectedAHU = currentProjectAHUs[index]

    // 고아 데이터 정리
    cleanupOrphanedLocalStorageData()

    dispatch({ type: 'SET_CURRENT_AHU', payload: selectedAHU.id })

    loadAHUFormData(selectedAHU)
  }

  const handleAddAHU = () => {
    // 프로젝트가 없으면 먼저 프로젝트를 생성하도록 안내
    if (!currentProject) {
      alert('먼저 프로젝트를 생성해주세요.\n\n상단의 "공조기 선정(새 프로젝트를 생성하세요)" 부분을 클릭하여 프로젝트를 생성할 수 있습니다.')
      return
    }

    // AHU 추가 대화상자 열기
    setIsAddAHUDialogOpen(true)
  }

  const handleCreateAHU = (ahuInfo: AHUEquipmentInfo) => {
    if (!state.currentProjectId) {
      alert('프로젝트가 선택되지 않았습니다.')
      return
    }

    // AHU 번호 중복 검사
    const existingAHU = currentProjectAHUs.find(ahu => ahu.ahuNo === ahuInfo.ahuNo)
    if (existingAHU) {
      alert(`AHU 번호 '${ahuInfo.ahuNo}'는 이미 존재합니다. 다른 번호를 사용해주세요.`)
      return
    }

    // 기본값만 사용하여 새 AHU 생성 (기존 폼 데이터 복사하지 않음)
    const defaultFormData = {
      // 새 AHU 기본 정보
      ahuNo: ahuInfo.ahuNo,
      systemName: ahuInfo.systemName,
      quantity: ahuInfo.quantity,
      location: ahuInfo.location,
      
      // (1) 공간 정보 기본값
      conditionedArea: '0',       // 공조면적 (m²)
      ceilingHeight: '2.7',       // 층고 (m) 
      occupantCount: '0',         // 인원수 (명)

      // (2) 공조 부하 기본값
      sensibleCoolingLoad: '0',   // 냉방 현열 부하(W)
      latentCoolingLoad: '0',     // 냉방 잠열 부하(W)
      ventilationLightingLoad: '0', // 환기 조명부하(W)
      heatingLoad: '0',           // 난방 부하(W)
      raLightingLoadMode: 'separate' as const, // RA Lighting Load 모드 (기본값: RA 온도상승)
      
      outdoorDesignCondition: 'A',
      outdoorTimeApplication: 'standard' as const, // 기본값: 표준 적용
      outdoorApplicationTime: '15', // 냉방 외기 적용 시각
      systemType: 'CAV',
      fanType: 'None',
      coolingCoilType: 'Water',
      reheatingCoilType: 'Water',
      preheatingCoilType: 'None',
      heatingCoilType: 'Water',
      commonCoil: false,
      coolingInletTemp: '7',
      coolingOutletTemp: '12',
      coolingSafetyFactor: '10',
      reheatingInletTemp: '60',
      reheatingOutletTemp: '50',
      reheatingSafetyFactor: '10',
      rhReheatTemp: '0',
      preheatingInletTemp: '80',
      preheatingOutletTemp: '70',
      preheatingSafetyFactor: '',
      heatingInletTemp: '80',
      heatingOutletTemp: '70',
      heatingSafetyFactor: '10',
      reheatingHeatCapacity: '',
      preheatingHeatCapacity: '',
      heatingHeatCapacity: '',
      supplyAirTemp: '18',
      humidifierType: 'None',
      humidifierInstallLocation: false,
      waterSupplyTemp: '15',
      humidifierSafetyFactor: '10',
      supplyAirflow: '',
      ventilationAirflow: '',
      exhaustAirflow: '',
      exhaustPurpose: '',
      pressurizationAirflow: '',
      supplyTempDelta: '10',
      saFanTempDelta: '0.7',
      airflowPerPerson: '25',
      directOutdoorAirflow: '',
      
      // 풍량 선정 기준
      supplyAirSelectionCriteria: 'standard',
      outdoorAirSelectionCriteria: 'direct',

      // 배열 회수기 설정
      heatRecoveryType: 'None',
      coolingRecoveryEfficiency: '70',
      heatingRecoveryEfficiency: '70',

      // SF/RF 부하 적용 기본값
      sfLoadEnabled: true,
      sfLoadPercentage: '100',
      rfLoadEnabled: true,
      rfLoadPercentage: '100',

      // 실내 온습도 (난방용 기본값)
      heatingRoomTemp: '20',
      heatingRoomHumidity: '40',

      supplyFan: createDefaultFanData(),
      returnFan: createDefaultFanData(),
    }

    const ahuData: AHUData = {
      id: generateUUID(),
      projectId: state.currentProjectId!,
      ahuNo: ahuInfo.ahuNo,
      systemName: ahuInfo.systemName,
      quantity: ahuInfo.quantity,
      location: ahuInfo.location,
      formData: defaultFormData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    dispatch({ type: 'ADD_AHU', payload: ahuData })

    // 다이얼로그는 AddAHUDialog의 onSubmit에서 onClose()로 닫힘
  }

  const handleDeleteAHU = (index: number) => {
    const ahuToDelete = currentProjectAHUs[index]
    const isCurrentAHU = index === currentAHUIndex
    const currentOrSelected = isCurrentAHU ? '(현재 선택된 공조기)' : ''

    if (window.confirm(`공조기 "${ahuToDelete.ahuNo}"${currentOrSelected}를(을) 삭제하시겠습니까?\n\n※ 삭제된 공조기의 모든 설정 데이터가 영구히 삭제됩니다.`)) {
      dispatch({ type: 'DELETE_AHU', payload: ahuToDelete.id })

      // 현재 AHU가 삭제된 경우 폼의 기본값 리셋
      if (isCurrentAHU) {
        const remainingAHUs = currentProjectAHUs.filter(ahu => ahu.id !== ahuToDelete.id)
        if (remainingAHUs.length === 0) {
          setValue('ahuNo', generateNextAHUNo())
          setValue('systemName', '')
          setValue('quantity', '1')
          setValue('location', '')
        }
      }
    }
  }

  // 글로벌 상태가 로드되기 전까지는 로딩 표시
  if (!state.isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography component="div">상태 복원 중...</Typography>
      </Box>
    );
  }

  return (
    <>
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 프로젝트 정보 섹션 */}
      <Paper elevation={2} sx={{ mb: 1 }}>
        {/* 프로젝트 정보 헤더 (항상 표시) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            p: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            gap: { xs: 1, sm: 0 }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 0.5, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              cursor: 'pointer'
            }}
            onClick={toggleProjectInfo}
          >
            {/* 첫 번째 줄: Home 버튼 + 아이콘 + 공조기 선정(프로젝트명) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Home 버튼 - 둥근 모서리 정사각형 스타일 */}
              {/* meptoolhub iframe embed 시에는 부모가 home 동작을 제공하므로 숨김 */}
              {!isEmbeddedByMeptoolhub() && (
                <Box
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateToHome();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    bgcolor: '#D7E3FF',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      bgcolor: '#C5D5FF',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                  role="button"
                  aria-label="Home"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      navigateToHome();
                    }
                  }}
                >
                  <HomeIcon sx={{ fontSize: 24, color: '#000000' }} />
                </Box>
              )}

              {/* <BusinessIcon /> */}
              <Typography variant="h6" component="div" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                공조기 선정
              </Typography>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  opacity: 0.9,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  wordBreak: 'break-word'
                }}
              >
                ({currentProject?.projectName || '새 프로젝트를 생성하세요'})
              </Typography>
            </Box>

            {/* 두 번째 줄: 건물명 | 위치 (모바일에서는 아래로) */}
            <Typography
              variant="body2"
              component="div"
              sx={{
                opacity: 0.8,
                ml: { xs: 0, sm: 2 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              {currentProject?.buildingName || '건물명 없음'} | {currentProject?.location || '위치 없음'}
            </Typography>
          </Box>

          

          {/* 파일 메뉴와 확장 버튼 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* 파일 메뉴 버튼 */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleFileMenuOpen}
              sx={{
                color: 'inherit',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              파일
            </Button>

            {/* 파일 메뉴 */}
            <Menu
              anchorEl={fileMenuAnchor}
              open={isFileMenuOpen}
              onClose={handleFileMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180
                }
              }}
            >
              <MenuItem onClick={handleOpenFileDialog}>
                <UploadFileIcon sx={{ mr: 1 }} />
                프로젝트 열기
              </MenuItem>
              <MenuItem onClick={handleProjectExport}>
                <DownloadIcon sx={{ mr: 1 }} />
                프로젝트 내보내기
              </MenuItem>
              <MenuItem onClick={handleResetRequest}>
                <RestartAltIcon sx={{ mr: 1 }} />
                초기화
              </MenuItem>

            </Menu>

            {/* 숨겨진 파일 입력 */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".dcs,.json"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <IconButton
              onClick={() => setIsProjectInfoExpanded(!isProjectInfoExpanded)}
              sx={{
                color: 'inherit',
                alignSelf: { xs: 'flex-end', sm: 'center' },
                mt: { xs: -4, sm: 0 }
              }}
            >
              {isProjectInfoExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* 프로젝트 상세 정보 (접을 수 있음) */}
        {isProjectInfoExpanded && (
          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Grid container spacing={2}>
              {/* 건물 개요 */}
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon />
                    건물 개요
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>프로젝트명</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={currentProject?.projectName || ''}
                        InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>건물명</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={currentProject?.buildingName || ''}
                        InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>위치</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={currentProject?.location || ''}
                        InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>건물 용도</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={currentProject?.buildingType || ''}
                        InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>지상층</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={currentProject?.totalFloors || ''}
                        InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>지하층</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={currentProject?.basementFloors || ''}
                        InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>연면적(㎡)</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={currentProject?.buildingArea ? Number(currentProject.buildingArea).toLocaleString() : ''}
                        InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* 온습도 조건 */}
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ThermostatIcon />
                    온습도 조건
                  </Typography>
                  <Grid container spacing={2}>
                    {/* 외기조건 - 여름/겨울/일교차 한 줄로 */}
                    <Grid item xs={12}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>외기조건</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="caption" component="span" sx={{ color: 'primary.main', fontWeight: 'bold', minWidth: 60 }}>여름철:</Typography>
                        <TextField
                          size="small"
                          sx={{ width: 70 }}
                          value={currentProject?.summerOutdoorTemp || ''}
                          InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                        />
                        <Typography variant="caption" component="span">℃</Typography>
                        <TextField
                          size="small"
                          sx={{ width: 70 }}
                          value={currentProject?.summerOutdoorHumidity || ''}
                          InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                        />
                        <Typography variant="caption" component="span">%</Typography>
                        <Typography variant="caption" component="span" sx={{ mx: 0.5 }}>|</Typography>
                        <Typography variant="caption" component="span">일교차</Typography>
                        <TextField
                          size="small"
                          sx={{ width: 70 }}
                          value={currentProject?.summerDailyRange || ''}
                          InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                        />
                        <Typography variant="caption" component="span" sx={{ mr: 2 }}>℃</Typography>

                        <Typography variant="caption" component="span" sx={{ color: 'error.main', fontWeight: 'bold', minWidth: 60 }}>겨울철:</Typography>
                        <TextField
                          size="small"
                          sx={{ width: 70 }}
                          value={currentProject?.winterOutdoorTemp || ''}
                          InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                        />
                        <Typography variant="caption" component="span">℃</Typography>
                        <TextField
                          size="small"
                          sx={{ width: 70 }}
                          value={currentProject?.winterOutdoorHumidity || ''}
                          InputProps={{ readOnly: true, sx: { bgcolor: 'grey.100' } }}
                        />
                        <Typography variant="caption" component="span">%</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
        
      </Paper>

      {/* 베타 알림 메시지 */}
          { <div className="beta-notice">
            <div className="beta-notice-content">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <div>
                <strong>베타 버전 안내</strong> - 본 앱은 현재 개발 및 테스트 단계입니다. 실무 적용 전 충분한 검증이 필요합니다.
              </div>
            </div>
          </div> }

      {/* AHU 관리 섹션 */}
      <Paper elevation={1} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div">공조기 관리</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
              {currentProjectAHUs.length === 0 ? '0/0' : `${currentAHUIndex + 1}/${currentProjectAHUs.length}`}
            </Typography>
            <IconButton size="small" onClick={handlePreviousAHU} disabled={currentProjectAHUs.length === 0 || currentAHUIndex <= 0}>
              <ArrowBackIcon />
            </IconButton>
            <IconButton size="small" onClick={handleNextAHU} disabled={currentProjectAHUs.length === 0 || currentAHUIndex >= currentProjectAHUs.length - 1}>
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>

        {/* AHU 번호 수평 목록 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {currentProjectAHUs.length === 0 ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              py: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'grey.300'
            }}>
              <Typography variant="body2" component="div" color="text.secondary" sx={{ mr: 2 }} >
                등록된 공조기가 없습니다. 새 공조기를 추가하세요.
              </Typography>
            </Box>
          ) : (
            currentProjectAHUs.map((ahu, index) => (
              <Chip
                key={ahu.id}
                label={ahu.ahuNo}
                variant={currentAHUIndex === index ? "filled" : "outlined"}
                color={currentAHUIndex === index ? "primary" : "default"}
                onClick={() => handleSelectAHU(index)}
                onDelete={() => handleDeleteAHU(index)}
                deleteIcon={<DeleteIcon />}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              />
            ))
          )}
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={handleAddAHU}
            sx={{
              height: '32px',
              borderRadius: '20px',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              minWidth: 'auto',
              px: 2,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            AHU 추가
          </Button>
        </Box>

        {/* 현재 선택된 AHU 정보 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }} component="div">
            현재 선택: {currentProjectAHUs.length === 0 ? '공조기 없음' : currentProjectAHUs[currentAHUIndex]?.ahuNo || '공조기 없음'}
          </Typography>
         
        </Box>
      </Paper>

      {/* Tab Headers */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="AHU selection tabs"
        >
          <Tab
            label="장비 기준"
            id="ahu-tab-0"
            disabled={!currentProject}
          />
          <Tab
            label="풍량 선정"
            id="ahu-tab-1"
            disabled={!currentProject}
          />
          <Tab
            label="Fan 선정"
            id="ahu-tab-2"
            disabled={!currentProject}
          />
          <Tab
            label="Output Sheets"
            id="ahu-tab-3"
            disabled={!currentProject}
          />
        </Tabs>
      </Box>

      {/* 빈 상태 안내 메시지 컴포넌트 */}
      {(!currentProject || currentProjectAHUs.length === 0) && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          textAlign: 'center',
          bgcolor: 'grey.50',
          border: '2px dashed',
          borderColor: 'grey.300',
          borderRadius: 2,
          mx: 2,
          my: 3
        }}>
          {!currentProject ? (
            <>
              <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" component="div" color="text.secondary" sx={{ mb: 1 }}>
                프로젝트를 먼저 생성하세요
              </Typography>
              <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 3 }}>
                상단의 "공조기 선정(새 프로젝트를 생성하세요)" 부분을 클릭하여<br />
                프로젝트를 생성한 후 공조기를 추가할 수 있습니다.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setIsViewOnlyMode(false);
                  setEditingProject(null);
                  setIsNewProjectDialogOpen(true);
                }}
              >
                프로젝트 생성하기
              </Button>
            </>
          ) : currentProjectAHUs.length === 0 ? (
            <>
              <AddIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" component="div" color="text.secondary" sx={{ mb: 1 }}>
                공조기를 먼저 추가하세요
              </Typography>
              <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 3 }}>
                상단의 "AHU 추가" 버튼을 클릭하여 공조기를 추가한 후<br />
                장비 설정을 진행할 수 있습니다.
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={handleAddAHU}
              >
                공조기 추가하기
              </Button>
            </>
          ) : null}
        </Box>
      )}

      {(currentProject && currentProjectAHUs.length > 0) ? (
        <Container maxWidth={false} sx={{ width: '100%', px: 2, flex: '1 1 auto', bgcolor: 'background.default' }}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%', minHeight: 'calc(100vh - 400px)' }}>
            <Box sx={{ height: '100%', minHeight: '100%' }}>
              {/* Tab 0: 장비 기준 (Equipment Criteria) */}
              <TabPanel value={activeTab} index={0}>
                <EquipmentStandardsTab
                  control={control}
                  watch={watch}
                  formState={formState}
                  OUTDOOR_CONDITIONS={OUTDOOR_CONDITIONS}
                  selectedOutdoorCondition={selectedOutdoorCondition}
                  getCurrentOutdoorCondition={getCurrentOutdoorCondition}
                  commonCoil={commonCoil}
                  coolingCoilType={coolingCoilType}
                  reheatingCoilType={reheatingCoilType}
                  preheatingCoilType={preheatingCoilType}
                  heatingCoilType={heatingCoilType}
                  humidifierType={humidifierType}
                  coilValidationError={coilValidationError}
                  validateAHUNumber={validateAHUNumber}
                />
              </TabPanel>

              {/* Tab 1: 풍량 선정 (Airflow Selection) */}
              <TabPanel value={activeTab} index={1}>
                <AirflowSelectionTab
                  control={control}
                  formState={formState}
                  watch={watch}
                  supplyAirSelectionCriteria={supplyAirSelectionCriteria}
                  outdoorAirSelectionCriteria={outdoorAirSelectionCriteria}
                  selectedOutdoorCondition={selectedOutdoorCondition}
                  calculatedAirflows={calculatedAirflows}
                  ahuCalculationResults={ahuCalculationResults}
                  currentProject={currentProject}
                  humidifierType={humidifierType}
                  onPsychroDataCalculated={handlePsychroDataCalculated}
                  currentAHUId={state.currentAHUId}
                  currentAHUData={currentAHUData}
                />
              </TabPanel>

              {/* Tab 2: Fan 선정 (Fan Selection) */}
              <TabPanel value={activeTab} index={2}>
                <FanSelectionTab
                  calculatedSupplyAirflow={calculatedAirflows.supplyAirflow}
                  calculatedReturnAirflow={calculatedAirflows.returnAirflow}
                />
              </TabPanel>

              {/* Tab 3: Output Sheets */}
              <TabPanel value={activeTab} index={3}>
                <OutputSheetsTab
                  showSheetPreview={showSheetPreview}
                  setShowSheetPreview={setShowSheetPreview}
                  currentProjectAHUs={currentProjectAHUs}
                  projectName={currentProject?.projectName || ''}
                  ahuCalculationResults={ahuCalculationResults}
                  currentAHUId={state.currentAHUId}
                  psychrometricData={psychrometricData}
                  currentProject={currentProject}
                />
              </TabPanel>
            </Box>
          </form>
        </Container>
      ) : null}

      {/* 프로젝트 관리 모달 */}
      <ProjectManagementModal
        open={isProjectManagementOpen}
        onClose={() => setIsProjectManagementOpen(false)}
        projects={state.projects}
        currentProject={currentProject}
        onSelectProject={handleSelectProject}
        onCreateProject={() => {
          setIsViewOnlyMode(false)
          setIsNewProjectDialogOpen(true)
        }}
        onViewProject={handleViewProject}
        onEditProject={handleEditProject}
        onDeleteProject={handleDeleteProject}
        searchTerm={projectSearchTerm}
        onSearchTermChange={setProjectSearchTerm}
        menuAnchorEl={menuAnchorEl}
        onMenuClick={(event, projectId) => {
          setMenuAnchorEl(event.currentTarget)
          setSelectedProjectForMenu(projectId)
        }}
        onMenuClose={() => {
          setMenuAnchorEl(null)
          setSelectedProjectForMenu(null)
        }}
        selectedProjectForMenu={selectedProjectForMenu}
      />

      {/* AHU 추가 대화상자 */}
      <AddAHUDialog
        open={isAddAHUDialogOpen}
        onClose={() => setIsAddAHUDialogOpen(false)}
        onSave={handleCreateAHU}
        currentProject={currentProject}
        existingAHUs={currentProjectAHUs.map(ahu => ahu.ahuNo)}
      />

      {/* 초기화 확인 다이얼로그 */}
      <Dialog
        open={isResetDialogOpen}
        onClose={handleResetCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RestartAltIcon color="warning" />
            초기화 확인
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            현재 작업 중인 프로젝트를 초기화하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • 현재 선택된 프로젝트와 공조기 선택이 해제됩니다.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • 작성 중인 폼 데이터가 초기화됩니다.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • 초기화 후 프로젝트 관리 창이 열립니다.
          </Typography>
          {currentProject && (
            <Paper sx={{ p: 2, bgcolor: 'warning.50', border: 1, borderColor: 'warning.200' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                현재 프로젝트
              </Typography>
              <Typography variant="body2">
                {currentProject.projectName}
              </Typography>
              {currentProject.buildingName && (
                <Typography variant="body2" color="text.secondary">
                  {currentProject.buildingName}
                </Typography>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleResetCancel} color="inherit">
            취소
          </Button>
          <Button
            onClick={handleResetWithoutSave}
            color="warning"
            variant="outlined"
          >
            저장 안 함
          </Button>
          {currentProject && (
            <Button
              onClick={handleResetWithSave}
              color="primary"
              variant="contained"
              startIcon={<SaveIcon />}
            >
              저장 후 초기화
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 신규/수정/보기 프로젝트 다이얼로그 */}
      <NewProjectDialog
        open={isNewProjectDialogOpen}
        onClose={() => {
          setIsNewProjectDialogOpen(false)
          setEditingProject(null)
          setIsViewOnlyMode(false)
        }}
        onSave={editingProject ? handleUpdateProject : handleCreateNewProject}
        initialData={editingProject}
        isEditing={!!editingProject}
        isViewOnly={isViewOnlyMode}
      />
    </Box>

    {/* 플로팅 사용자 메뉴얼 버튼 */}
    <UserManualButton />
  </>
  )
}

// 신규 프로젝트 다이얼로그 컴포넌트
interface NewProjectDialogProps {
  open: boolean
  onClose: () => void
  onSave: (project: ProjectInfo) => void
  initialData?: ProjectInfo | null
  isEditing: boolean
  isViewOnly?: boolean
}

const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  isEditing,
  isViewOnly = false
}) => {
  const [currentTab, setCurrentTab] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const defaultOutdoorCondition = OUTDOOR_CONDITIONS.find(c => c.code === 'A') || OUTDOOR_CONDITIONS[0];

  // Validate and sanitize initialData
  const validBuildingTypes = ['상업시설', '업무시설', '주거시설', '의료시설', '교육시설', '문화시설', '공장시설', '기타']
  const sanitizedInitialData = initialData ? {
    ...initialData,
    buildingType: (initialData.buildingType && validBuildingTypes.includes(initialData.buildingType))
      ? initialData.buildingType
      : '상업시설'
  } : undefined

  const { control, handleSubmit, reset, watch, trigger, setValue, formState: { errors, isValid } } = useForm<ProjectInfo>({
    mode: 'onChange',
    defaultValues: sanitizedInitialData || {
      projectName: '',
      buildingName: '',
      location: '',
      buildingType: '상업시설',
      totalFloors: '1',
      basementFloors: '0',
      buildingArea: '',
      altitude: '0',
      outdoorConditionCode: '', // 초기값을 빈 문자열로 설정하여 사용자가 선택하도록 유도
      summerOutdoorTemp: '',
      summerOutdoorHumidity: '',
      summerDailyRange: '',
      winterOutdoorTemp: '',
      winterOutdoorHumidity: '',
      summerIndoorTemp: '26',
      summerIndoorHumidity: '55',
      winterIndoorTemp: '20',
      winterIndoorHumidity: '40'
    }
  })

  const selectedOutdoorCondition = watch('outdoorConditionCode')

  const steps = [
    { label: '기본 정보', icon: <BusinessIcon /> },
    { label: '건물 규모', icon: <HomeIcon /> },
    { label: '설계 조건', icon: <ThermostatIcon /> },
  ]

  useEffect(() => {
    if (open) {
      setCurrentTab(0)
      setIsSubmitting(false)

      const defaultData = {
        projectName: '',
        buildingName: '',
        location: '',
        buildingType: '상업시설',
        totalFloors: '1',
        basementFloors: '0',
        buildingArea: '',
        altitude: '0',
        outdoorConditionCode: '', // 초기값을 빈 문자열로 설정
        summerOutdoorTemp: '',
        summerOutdoorHumidity: '',
        summerDailyRange: '',
        winterOutdoorTemp: '',
        winterOutdoorHumidity: '',
        summerIndoorTemp: '26',
        summerIndoorHumidity: '55',
        winterIndoorTemp: '20',
        winterIndoorHumidity: '40'
      }

      if (initialData) {
        // initialData와 기본값을 병합하고, 누락된 필드는 기본값으로 채움
        const mergedData = {
          ...defaultData,
          ...initialData,
          // buildingType 검증
          buildingType: (initialData.buildingType && validBuildingTypes.includes(initialData.buildingType))
            ? initialData.buildingType
            : defaultData.buildingType,
          // 숫자 필드들을 문자열로 변환
          totalFloors: initialData.totalFloors?.toString() || defaultData.totalFloors,
          basementFloors: initialData.basementFloors?.toString() || defaultData.basementFloors,
          buildingArea: initialData.buildingArea?.toString() || defaultData.buildingArea,
          altitude: initialData.altitude?.toString() || defaultData.altitude,
          summerOutdoorTemp: initialData.summerOutdoorTemp?.toString() || defaultData.summerOutdoorTemp,
          summerOutdoorHumidity: initialData.summerOutdoorHumidity?.toString() || defaultData.summerOutdoorHumidity,
          summerDailyRange: initialData.summerDailyRange?.toString() || defaultData.summerDailyRange,
          winterOutdoorTemp: initialData.winterOutdoorTemp?.toString() || defaultData.winterOutdoorTemp,
          winterOutdoorHumidity: initialData.winterOutdoorHumidity?.toString() || defaultData.winterOutdoorHumidity,
          summerIndoorTemp: initialData.summerIndoorTemp?.toString() || defaultData.summerIndoorTemp,
          summerIndoorHumidity: initialData.summerIndoorHumidity?.toString() || defaultData.summerIndoorHumidity,
          winterIndoorTemp: initialData.winterIndoorTemp?.toString() || defaultData.winterIndoorTemp,
          winterIndoorHumidity: initialData.winterIndoorHumidity?.toString() || defaultData.winterIndoorHumidity,
        }
        reset(mergedData)
      } else {
        reset(defaultData)
      }
    }
  }, [open, initialData, reset])

  // 외기 조건 변경 시 자동으로 온습도 업데이트 (최초 렌더링 포함)
  useEffect(() => {
    if (open && selectedOutdoorCondition) {
      const condition = OUTDOOR_CONDITIONS.find(c => c.code === selectedOutdoorCondition)
      if (condition) {
        setValue('summerOutdoorTemp', condition.summer.temperature.toString())
        setValue('summerOutdoorHumidity', condition.summer.humidity.toString())
        setValue('summerDailyRange', condition.summer.dailyRange.toString())
        setValue('winterOutdoorTemp', condition.winter.temperature.toString())
        setValue('winterOutdoorHumidity', condition.winter.humidity.toString())
      }
    }
  }, [open, selectedOutdoorCondition, setValue])


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const onSubmit = async (data: ProjectInfo) => {

    // 외기 설계조건 필수 체크
    if (!data.outdoorConditionCode || data.outdoorConditionCode === '') {
      alert('외기 설계조건을 선택해주세요.');
      setCurrentTab(2); // 설계 조건 탭으로 이동
      return;
    }

    setIsSubmitting(true)
    try {
      const projectData = {
        ...data,
        createdAt: initialData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      await onSave(projectData)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderTabContent = () => {
    return (
      <>
        {/* 탭 0: 기본 정보 */}
        <Box sx={{ display: currentTab === 0 ? 'block' : 'none' }}>
          <Grid container spacing={3}>
            {!isViewOnly && (
              <Grid item xs={12}>
                <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 3 }}>
                  프로젝트의 기본 정보를 입력해주세요. 이 정보는 나중에 수정할 수 있습니다.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                name="projectName"
                control={control}
                rules={{
                  required: '프로젝트명을 입력하세요',
                  minLength: { value: 2, message: '프로젝트명은 2글자 이상이어야 합니다' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="프로젝트명 *"
                    fullWidth
                    disabled={isViewOnly}
                    error={!!errors.projectName}
                    helperText={errors.projectName?.message || '예: XX빌딩 신축공사'}
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>📋</Box>
                    }}
                    autoFocus
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="buildingName"
                control={control}
                rules={{ required: '건물명을 입력하세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="건물명 *"
                    fullWidth
                    disabled={isViewOnly}
                    error={!!errors.buildingName}
                    helperText={errors.buildingName?.message || '예: XX빌딩'}
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>🏢</Box>
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="location"
                control={control}
                rules={{ required: '위치를 입력하세요' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="위치 *"
                    fullWidth
                    disabled={isViewOnly}
                    error={!!errors.location}
                    helperText={errors.location?.message || '예: 서울특별시 강남구 역삼동'}
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>📍</Box>
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="buildingType"
                control={control}
                rules={{ required: '건물 용도를 선택하세요' }}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Typography variant="body2" component="div" sx={{ mb: 1, fontWeight: 'medium' }}>
                      🏗️ 건물 용도 *
                    </Typography>
                    <Select
                      {...field}
                      disabled={isViewOnly}
                      error={!!errors.buildingType}
                      displayEmpty
                    >
                      <MenuItem value="상업시설">🏬 상업시설 (백화점, 쇼핑몰)</MenuItem>
                      <MenuItem value="업무시설">🏢 업무시설 (오피스빌딩)</MenuItem>
                      <MenuItem value="주거시설">🏠 주거시설 (아파트, 주택)</MenuItem>
                      <MenuItem value="의료시설">🏥 의료시설 (병원, 클리닉)</MenuItem>
                      <MenuItem value="교육시설">🏫 교육시설 (학교, 학원)</MenuItem>
                      <MenuItem value="문화시설">🎭 문화시설 (박물관, 도서관)</MenuItem>
                      <MenuItem value="공장시설">🏭 공장시설 (제조업, 창고)</MenuItem>
                      <MenuItem value="기타">🏗️ 기타</MenuItem>
                    </Select>
                    {errors.buildingType && (
                      <Typography variant="caption" component="div" color="error" sx={{ mt: 0.5, ml: 1 }}>
                        {errors.buildingType.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 탭 1: 건물 규모 */}
        <Box sx={{ display: currentTab === 1 ? 'block' : 'none' }}>
          <Grid container spacing={3}>
            {!isViewOnly && (
              <Grid item xs={12}>
                <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 3 }}>
                  건물의 규모 정보를 입력해주세요. 정확한 HVAC 설계를 위해 필요합니다.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <Controller
                name="totalFloors"
                control={control}
                rules={{
                  required: '지상 층수를 입력하세요',
                  min: { value: 1, message: '지상 층수는 1층 이상이어야 합니다' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="지상 층수 *"
                    type="number"
                    fullWidth
                    disabled={isViewOnly}
                    error={!!errors.totalFloors}
                    helperText={errors.totalFloors?.message}
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>🔼</Box>,
                      endAdornment: <Box sx={{ ml: 1 }}>층</Box>
                    }}
                    inputProps={{ min: 1, max: 100 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="basementFloors"
                control={control}
                rules={{
                  min: { value: 0, message: '지하 층수는 0층 이상이어야 합니다' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="지하 층수"
                    type="number"
                    fullWidth
                    disabled={isViewOnly}
                    error={!!errors.basementFloors}
                    helperText={errors.basementFloors?.message || '없으면 0을 입력'}
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>🔽</Box>,
                      endAdornment: <Box sx={{ ml: 1 }}>층</Box>
                    }}
                    inputProps={{ min: 0, max: 20 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="altitude"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="해발 고도"
                    type="number"
                    fullWidth
                    disabled={isViewOnly}
                    helperText="외기 조건 보정용 (기본값: 0m)"
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>⛰️</Box>,
                      endAdornment: <Box sx={{ ml: 1 }}>m</Box>
                    }}
                    inputProps={{ min: 0, max: 3000 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="buildingArea"
                control={control}
                rules={{
                  required: '연면적을 입력하세요',
                  min: { value: 1, message: '연면적은 1m² 이상이어야 합니다' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="연면적 *"
                    type="text"
                    fullWidth
                    disabled={isViewOnly}
                    error={!!errors.buildingArea}
                    helperText={errors.buildingArea?.message || '건물의 총 바닥면적을 입력하세요'}
                    value={field.value ? Number(field.value).toLocaleString() : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      if (value === '' || /^\d+$/.test(value)) {
                        field.onChange(value === '' ? '' : Number(value));
                      }
                    }}
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>📐</Box>,
                      endAdornment: <Box sx={{ ml: 1 }}>m²</Box>
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 탭 2: 설계 조건 */}
        <Box sx={{ display: currentTab === 2 ? 'block' : 'none' }}>
          {(() => {
            const selectedCondition = OUTDOOR_CONDITIONS.find(c => c.code === selectedOutdoorCondition)
            return (
              <Grid container spacing={3}>
            {!isViewOnly && (
              <Grid item xs={12}>
                <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 3 }}>
                  HVAC 설계를 위한 외기 설계조건을 설정해주세요.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                name="outdoorConditionCode"
                control={control}
                rules={{ required: '외기 설계조건을 선택하세요' }}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Typography variant="body2" component="div" sx={{ mb: 1, fontWeight: 'medium' }}>
                      🌡️ 외기 설계조건 *
                    </Typography>
                    <Select
                      {...field}
                      disabled={isViewOnly}
                      error={!!errors.outdoorConditionCode}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <Typography color="text.secondary">외기 설계조건을 선택하세요</Typography>
                      </MenuItem>
                      {OUTDOOR_CONDITIONS.map(condition => (
                        <MenuItem key={condition.code} value={condition.code}>
                          <Box>
                            <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                              {condition.code} - {condition.location}
                            </Typography>
                            <Typography variant="caption" component="span" color="text.secondary">
                              여름: {condition.summer.temperature}℃, {condition.summer.humidity}% |
                              겨울: {condition.winter.temperature}℃, {condition.winter.humidity}%
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* 여름철/겨울철 외기조건을 2행으로 배치 */}
            <Grid item xs={12}>
              <Box sx={{ pt: 2, px: 2, pb: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                <Grid container spacing={1.5}>
                  {/* 여름철 외기조건 */}
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                        ☀️ 여름철 외기조건
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="summerOutdoorTemp"
                            control={control}
                            rules={{
                              required: '여름철 외기온도를 입력하세요',
                              min: { value: 20, message: '여름철 외기온도는 20℃ 이상이어야 합니다' },
                              max: { value: 45, message: '여름철 외기온도는 45℃ 이하여야 합니다' }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="외기온도 *"
                                type="number"
                                fullWidth
                                disabled={isViewOnly}
                                error={!!errors.summerOutdoorTemp}
                                helperText={errors.summerOutdoorTemp?.message}
                                InputProps={{
                                  startAdornment: <Box sx={{ mr: 1 }}>🌡️</Box>,
                                  endAdornment: <Box sx={{ ml: 1 }}>℃</Box>
                                }}
                                inputProps={{ min: 20, max: 45, step: 0.1 }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="summerOutdoorHumidity"
                            control={control}
                            rules={{
                              required: '여름철 외기습도를 입력하세요',
                              min: { value: 0, message: '습도는 0% 이상이어야 합니다' },
                              max: { value: 100, message: '습도는 100% 이하여야 합니다' }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="상대습도 *"
                                type="number"
                                fullWidth
                                disabled={isViewOnly}
                                error={!!errors.summerOutdoorHumidity}
                                helperText={errors.summerOutdoorHumidity?.message}
                                InputProps={{
                                  startAdornment: <Box sx={{ mr: 1 }}>💧</Box>,
                                  endAdornment: <Box sx={{ ml: 1 }}>%</Box>
                                }}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Controller
                            name="summerDailyRange"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="일교차"
                                type="number"
                                fullWidth
                                disabled={isViewOnly}
                                helperText="여름철 일교차 (기본값: 10℃)"
                                InputProps={{
                                  startAdornment: <Box sx={{ mr: 1 }}>📊</Box>,
                                  endAdornment: <Box sx={{ ml: 1 }}>℃</Box>
                                }}
                                inputProps={{ min: 5, max: 20, step: 0.5 }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* 겨울철 외기조건 */}
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mb: 2, color: 'error.main' }}>
                        ❄️ 겨울철 외기조건
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="winterOutdoorTemp"
                            control={control}
                            rules={{
                              required: '겨울철 외기온도를 입력하세요',
                              min: { value: -30, message: '겨울철 외기온도는 -30℃ 이상이어야 합니다' },
                              max: { value: 20, message: '겨울철 외기온도는 20℃ 이하여야 합니다' }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="외기온도 *"
                                type="number"
                                fullWidth
                                disabled={isViewOnly}
                                error={!!errors.winterOutdoorTemp}
                                helperText={errors.winterOutdoorTemp?.message}
                                InputProps={{
                                  startAdornment: <Box sx={{ mr: 1 }}>🌡️</Box>,
                                  endAdornment: <Box sx={{ ml: 1 }}>℃</Box>
                                }}
                                inputProps={{ min: -30, max: 20, step: 0.1 }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="winterOutdoorHumidity"
                            control={control}
                            rules={{
                              required: '겨울철 외기습도를 입력하세요',
                              min: { value: 0, message: '습도는 0% 이상이어야 합니다' },
                              max: { value: 100, message: '습도는 100% 이하여야 합니다' }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="상대습도 *"
                                type="number"
                                fullWidth
                                disabled={isViewOnly}
                                error={!!errors.winterOutdoorHumidity}
                                helperText={errors.winterOutdoorHumidity?.message}
                                InputProps={{
                                  startAdornment: <Box sx={{ mr: 1 }}>💧</Box>,
                                  endAdornment: <Box sx={{ ml: 1 }}>%</Box>
                                }}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
            )
          })()}
        </Box>
      </>
    )
  }


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            {isViewOnly ? <InfoIcon color="primary" /> : isEditing ? <EditIcon color="primary" /> : <AddIcon color="primary" />}
            <Typography variant="h5" component="span">
              {isViewOnly ? '프로젝트 정보 보기' : isEditing ? '프로젝트 수정' : '새 프로젝트 생성'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {isViewOnly ? (
          /* 보기 전용 레이아웃 */
          <Box>
            {/* 기본 정보 섹션 */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessIcon color="primary" />
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  기본 정보
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" component="div">프로젝트명</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('projectName') || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" component="div">건물명</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('buildingName') || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" component="div">위치</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('location') || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" component="div">건물 용도</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('buildingType') || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* 건물 규모 섹션 */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HomeIcon color="primary" />
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  건물 규모
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" component="div">지상 층수</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('totalFloors') || '-'}층
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" component="div">지하 층수</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('basementFloors') || '-'}층
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" component="div">연면적</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('buildingArea') ? Number(watch('buildingArea')).toLocaleString() : '-'}m²
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="text.secondary" component="div">해발 고도</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                    {watch('altitude') || '0'}m
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* 외기 설계조건 섹션 */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ThermostatIcon color="primary" />
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  외기 설계조건
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" component="div">설계조건 지역</Typography>
                  <Typography variant="body1" component="div" sx={{ fontWeight: 'medium', mb: 2 }}>
                    {OUTDOOR_CONDITIONS.find(c => c.code === watch('outdoorConditionCode'))?.location || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: '#E3F2FD', borderRadius: 1, border: 1, borderColor: '#64B5F6', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mb: 2, color: '#0D47A1' }}>
                      ☀️ 여름철 외기조건
                    </Typography>
                    <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" component="div">외기온도</Typography>
                        <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                          {watch('summerOutdoorTemp') || '-'}℃
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" component="div">상대습도</Typography>
                        <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                          {watch('summerOutdoorHumidity') || '-'}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" component="div">일교차</Typography>
                        <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                          {watch('summerDailyRange') || '-'}℃
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: '#FFF3E0', borderRadius: 1, border: 1, borderColor: '#FFB74D', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold', mb: 2, color: '#E65100' }}>
                      ❄️ 겨울철 외기조건
                    </Typography>
                    <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" component="div">외기온도</Typography>
                        <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                          {watch('winterOutdoorTemp') || '-'}℃
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" component="div">상대습도</Typography>
                        <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                          {watch('winterOutdoorHumidity') || '-'}%
                        </Typography>
                      </Grid>
                      {/* 높이 맞추기 위한 빈 공간 */}
                      <Grid item xs={12} sx={{ minHeight: '48px' }}>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        ) : (
          /* 입력/수정 모드 레이아웃 */
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
              >
                {steps.map((step, index) => (
                  <Tab
                    key={index}
                    label={step.label}
                    icon={step.icon}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Box>
            {renderTabContent()}
          </>
        )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            startIcon={<CloseIcon />}
            type="button"
          >
            {isViewOnly ? '닫기' : '취소'}
          </Button>

          {!isViewOnly && (
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? undefined : <SaveIcon />}
              endIcon={isSubmitting ? <Box sx={{ ml: 1 }}>⏳</Box> : undefined}
            >
              {isSubmitting ? '저장 중...' : (isEditing ? '수정 완료' : '프로젝트 생성')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default AHUSelectionForm
